// src/pages/ProtokollFormPage.jsx
// Wartungsprotokoll-Editor v2: Lokaler State, debounced save,
// Multi-Select-Modifier bei n.i.O., Stammdaten je Tab.

import { useState, useEffect, useRef, useCallback } from 'react';
import { useProtokoll, savePruefpunkt, trafoNachtragen, alsBegonnenMarkieren, protokollZwischenspeichern, verteilungNachtragen, verteilungUmbenennen, verteilungEntfernen } from '../hooks/useProtokoll';
import { useAuth } from '../hooks/useAuth';
import { useStation } from '../hooks/useStationen';
import { istSichtbar, istBeantwortet, pruefFristHinweis, anzeigeNummern, anzeigeNummer, trafoDesKapitels, kapitelAusgeblendet } from '../lib/pruefpunktLogik';
import { lebensdauerBefund, LEBENSDAUER_JAHRE } from '../lib/broschueren';
import { anlagenLage, regelFuer, kapitelHinweise, optionNichtErforderlich } from '../lib/anlagenRegeln';
import { fotosAbarbeiten, fotoZaehler, beiFotoAenderung } from '../lib/fotoWarteschlange';
import { VorWartungContext, useVorWartung, useVorWartungLaden, vorWartungSchluessel, datumKurz } from '../lib/vorWartung';
import { anzahl as wartendeAnzahl, beiAenderung, abarbeiten, starteAutomatik, jetztSpeichern, abgelehnteErneutSenden, abgelehnteLesen, abgelehnteAnzahl, speicherIstVoll, abschlussAbgelehnt } from '../lib/warteschlange';
import { C, S, NumberInput, TextInput, DatumFeld, datumAnzeige, zurueckStil } from './formui';
import { infoFuer } from '../lib/pruefpunktInfo';
import { t, ppText, optionText, modulName, kategorieText, zusatzLabel, useSprache } from '../lib/sprache';
import { EditableStammdaten, WANDLER_ZAEHLER_CODE } from './EditableStammdaten';
import AbschlussBericht from './AbschlussBericht';
import { FotoUploadPruefpunkt } from './Fotos';
import { UESP_POS, UESP_MODUL, uespBewertung, sollNachschlagen, sollMerken } from '../lib/ueberspannung';
import { absprachenAusCache, absprachenLaden, ABSCHALTZEITEN, LEISTUNGEN, leistungGewaehlt, OELPROBE_VERTRAG } from '../lib/vertraege';

// Heuristik: ist Option "primaer" (i.O./n.i.O./n.benötigt/ja/nein)?
//
// Leerzeichen werden vor dem Vergleich entfernt. Grund: In den aus dem Excel
// importierten Vorlagen steht bei Pruefpunkt 02.11 "n. i.O." MIT Leerzeichen,
// bei allen anderen 111 Punkten "n.i.O.". Ohne diese Toleranz fiel die Option
// durch die Erkennung und erschien gar nicht als Antwortknopf -- der Monteur
// konnte dort nur "i.O." oder "n. benoetigt" waehlen.
function norm(opt) {
  return (opt || '').toLowerCase().replace(/\s+/g, '');
}
function isPrimaryPositive(opt) {
  const lo = norm(opt);
  // startsWith: Auch "i.O. – Schwingungsdämpfer statt Rollsperre" (04.8) ist
  // eine Gut-Antwort und wird gruen. Anna-Lena nach dem Testlauf 13.09.2026.
  // "vorhanden" zaehlt wie "ja" (01.6.1 Glasbausteine/Fenster, Migration 50 -
  // dort dreht logik_invertiert es auf rot).
  return (lo.startsWith('i.o.') || lo === 'i.o' || lo === 'ja' || lo === 'vorhanden');
}
function isPrimaryNegative(opt) {
  const lo = norm(opt);
  return (lo.includes('n.i.o') || lo === 'nein' || lo === 'nichtvorhanden');
}
function isPrimaryNeutral(opt) {
  const lo = norm(opt);
  return lo.includes('benoetigt') || lo.includes('benötigt') || lo === 'entfaellt' || lo === 'entfällt';
}
function isPrimary(opt) {
  return isPrimaryPositive(opt) || isPrimaryNegative(opt) || isPrimaryNeutral(opt);
}

// Pruefpunkte, bei denen die App die Begruendung SELBST liefert und rechnet.
// Dort wird kein Beleg verlangt -- der Grund steht ja bereits im Protokoll:
//   02.12         Lebenszyklus: die App nennt das Baujahr und das Alter
//                 (03.3 nicht mehr: dort gibt es keine Baujahre - also Beleg noetig)
//   05.1.5        Oelwanne: die App rechnet Auffangvolumen gegen Oelmenge
//   06.3          Erdungswiderstand: der gemessene Ohm-Wert steht an der Schiene
// Anna-Lena am 03.09.2026: "Wenn ich sage, sie ist zu alt, dann ist der Grund,
// dass sie zu alt ist -- da brauchen wir keine Bemerkung als Pflichtfeld."
// 01.6.1: "vorhanden" IST der Grund (Anna-Lena 18.09.2026: kein Grund notwendig).
// 05.9/05.31: bewertet die App (Schilder ist/soll, Pruefdatum). 05.30 Feuerloescher:
// kein Grund noetig (Anna-Lena 18.09.2026).
const BEGRUENDUNG_KOMMT_VON_DER_APP = ['02.12', '05.1.5', '06.3', '01.6.1', '05.9', '05.30', '05.31', '03.5', '04.12'];

// Hier ist "nein" KEIN Mangel, sondern nur die andere Bauart der Ölwanne
// (Anna-Lena 15.09.2026): keine Blechwanne -> Beschichtung, keine
// Beschichtung -> Blechwanne. Fehlt die Wanne ganz, ist 05.1 "nein" der Mangel.
// 03 "NSHV nicht vorhanden" ist ebenfalls kein Mangel (Migration 52).
const NEIN_OHNE_MANGEL = ['05.1.1', '05.2.1', '03'];

// Kategorien, die als kompakte Gruppe erscheinen: je Punkt eine Zeile mit
// Stichwort und Knoepfen, oben das Gesamtergebnis (Anna-Lena 22.09.2026: "damit das
// Kapitel nicht so gross wirkt und schneller zu bearbeiten ist"). Weitere Gruppen
// lassen sich hier einfach ergaenzen.
const KOMPAKT_KATEGORIEN = new Set(['Türen und Schlösser', 'Doppelboden']);

// Zusatz-Felder Renderer (z.B. Stk. soll/ist, Bemessungsstrom)
//
// Mit Verzoegerung wie die Bemerkung (Pruefung 20.09.2026): vorher ging bei
// JEDEM Tastendruck eine eigene Anfrage raus - "Gloor 1000 A" waren elf
// Speichervorgaenge hintereinander. Was noch offen ist, wird beim Speichern-
// Knopf, beim Verlassen und beim Wechsel in den Hintergrund sofort abgegeben.
function ZusatzFelder({ pp, onChange }) {
  const felder = Array.isArray(pp.zusatz_felder) ? pp.zusatz_felder : [];
  const werte = pp.zusatz_werte || {};
  const [lokal, setLokal] = useState(null);      // null = nichts offen
  const offen = useRef(null);
  const timer = useRef(null);
  const werteRef = useRef(werte);
  werteRef.current = werte;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const abgeben = () => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    if (offen.current) {
      const v = offen.current;
      offen.current = null;
      onChangeRef.current({ zusatz_werte: v });
    }
  };
  useEffect(() => {
    window.addEventListener('etb-jetzt-speichern', abgeben);
    return () => { window.removeEventListener('etb-jetzt-speichern', abgeben); abgeben(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Kommt von aussen ein neuer Stand und nichts ist offen: wieder die Vorgabe zeigen.
  useEffect(() => { if (offen.current === null) setLokal(null); }, [pp.zusatz_werte]);

  if (felder.length === 0) return null;
  const zeigt = lokal || werte;
  const setWert = (key, val) => {
    const neu = { ...(offen.current || werteRef.current), [key]: val };
    offen.current = neu;
    setLokal(neu);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(abgeben, 500);
  };
  return (
    <div style={{ marginTop: 8, padding: 10, background: C.white, borderRadius: 7, border: '1px solid ' + C.gray200 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: C.gray500, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>{t('Zusatzangaben')}</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 8 }}>
        {felder.map(f => (
          <div key={f.key}>
            <div style={{ fontSize: 13, fontWeight: 600, color: C.gray700, marginBottom: 3 }}>{zusatzLabel(f.label)}</div>
            {f.typ === 'number' ? (
              <NumberInput value={zeigt[f.key]} onChange={v => setWert(f.key, v)}/>
            ) : f.typ === 'date' ? (
              // Eintippen statt Kalender blaettern: Baujahre und Pruefdaten
              // liegen oft 20-30 Jahre zurueck (Testlauf 13.09.2026).
              <DatumFeld value={zeigt[f.key]} onChange={v => setWert(f.key, v)}/>
            ) : (
              <TextInput value={zeigt[f.key]} onChange={v => setWert(f.key, v)}/>
            )}
          </div>
        ))}
      </div>
      {/* Prueffrist ueberschritten oder Fehlbestand: gleich hier sagen,
          nicht erst im PDF. Werte aus der eigenen Broschuere 15. */}
      {(() => {
        const hinweis = pruefFristHinweis(pp.pos_nummer, zeigt);
        if (!hinweis) return null;
        return (
          <div style={{ marginTop: 8, padding: '8px 10px', background: C.orangeBg,
                        borderLeft: '3px solid ' + C.orange, borderRadius: 6,
                        fontSize: 14, color: C.navy, fontWeight: 600 }}>
            {hinweis}
          </div>
        );
      })()}
    </div>
  );
}


// Trafo waehrend der Wartung nachtragen.
// Steht der Monteur vor einem Transformator, den niemand in den Stammdaten
// erfasst hat, gab es bisher keinen Weg: kein Modul, kein Knopf, nichts.
// Er musste die Wartung abbrechen und im Buero anrufen.
function TrafoNachtragen({ protokollId, stationId, onFertig }) {
  const [offen, setOffen] = useState(false);
  const [name, setName] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState(null);

  const anlegen = async () => {
    setLaeuft(true); setFehler(null);
    try {
      await trafoNachtragen(protokollId, stationId, name);
      setOffen(false); setName('');
      if (onFertig) onFertig();
    } catch (e) {
      setFehler(e.message || t('Der Trafo konnte nicht angelegt werden.'));
    } finally {
      setLaeuft(false);
    }
  };

  if (!protokollId || !stationId) return null;

  if (!offen) {
    return (
      <button type='button' onClick={() => setOffen(true)}
        style={{ width: '100%', minHeight: 52, padding: '12px 16px', marginTop: 12,
                 borderRadius: 10, border: '2px dashed ' + C.gray300, background: C.white,
                 color: C.navy, fontWeight: 700, fontSize: 15, cursor: 'pointer',
                 fontFamily: 'inherit' }}>
        {t('+ Trafo fehlt? Hier nachtragen')}
      </button>
    );
  }

  return (
    <div style={{ marginTop: 12, padding: 14, background: C.white,
                  border: '2px solid ' + C.navy, borderRadius: 10 }}>
      <div style={{ fontWeight: 800, color: C.navy, fontSize: 15, marginBottom: 4 }}>
        {t('Trafo nachtragen')}
      </div>
      <div style={{ fontSize: 14, color: C.gray500, marginBottom: 10, lineHeight: 1.4 }}>
        {t('Der Trafo wird in den Stammdaten der Station angelegt und bekommt sofort ein eigenes Kapitel in diesem Protokoll. Typenschilddaten kannst du anschliessend dort eintragen.')}
      </div>
      <TextInput value={name} onChange={setName} placeholder={t('Bezeichnung, z. B. "Trafo 2"')}/>
      {fehler && (
        <div style={{ marginTop: 8, padding: '8px 10px', background: C.redBg,
                      color: C.red, borderRadius: 6, fontSize: 14, fontWeight: 700 }}>
          {fehler}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
        <button type='button' onClick={anlegen} disabled={laeuft}
          style={{ minHeight: 48, padding: '11px 20px', borderRadius: 8, border: 'none',
                   background: laeuft ? C.gray300 : C.navy, color: C.white,
                   fontWeight: 800, fontSize: 15, cursor: laeuft ? 'wait' : 'pointer',
                   fontFamily: 'inherit' }}>
          {laeuft ? t('wird angelegt …') : t('Trafo anlegen')}
        </button>
        <button type='button' onClick={() => { setOffen(false); setFehler(null); }} disabled={laeuft}
          style={{ minHeight: 48, padding: '11px 18px', borderRadius: 8,
                   border: '1.5px solid ' + C.gray200, background: C.white, color: C.gray700,
                   fontWeight: 700, fontSize: 15, cursor: 'pointer', fontFamily: 'inherit' }}>
          {t('Abbrechen')}
        </button>
      </div>
    </div>
  );
}

// === Kapitel "Wandler / Zähler" (Anna-Lena 08.10.2026) =======================
// "Wandler als separaten Punkt (nicht unter MS-Anlage)". Das Kapitel kommt nicht
// aus der Datenbank (keine Pruefpunkte, nur Stammdaten) - die App setzt es
// hinter die MS-Anlage. So gilt es auch fuer schon angelegte Protokolle.
function mitWandlerKapitel(liste, protokollId) {
  if (!liste || liste.length === 0) return liste || [];
  const codes = liste.map(m => m?.modul_vorlagen?.code);
  const ms = codes.lastIndexOf('02-MS-ANLAGE');
  const nach = ms >= 0 ? ms : 0;
  const kapitel = {
    id: 'kapitel-wandler-zaehler', pseudo: true, protokoll_id: protokollId,
    modul_vorlagen: { code: WANDLER_ZAEHLER_CODE, name: 'Wandler / Zähler' }, pruefpunkte: [],
  };
  return [...liste.slice(0, nach + 1), kapitel, ...liste.slice(nach + 1)];
}

// Die angezeigten Kapitel: ohne Oelwanne eines Giessharz-Trafos, mit "Wandler / Zähler".
function angezeigteKapitel(alleModule, trafosBekannt, protokollId) {
  return mitWandlerKapitel(alleModule.filter(m => !kapitelAusgeblendet(m, trafosBekannt)), protokollId);
}

function WandlerZaehlerPanel({ modul, station, config, wandler, onStationReload, stationFehler, gesperrt }) {
  return (
    <div style={{ background: C.white, borderRadius: 10, padding: 'clamp(14px, 3vw, 22px)', boxShadow: '0 1px 6px rgba(10,22,40,0.06)' }}>
      <div style={{ fontWeight: 800, color: C.navy, fontSize: 16, marginBottom: 6 }}>{t('Wandler / Zähler')}</div>
      <div style={{ fontSize: 14, color: C.gray500, marginBottom: 12, lineHeight: 1.4 }}>
        {t('Strom- und Spannungswandler sowie Zähler der Station. Änderungen gehen direkt in die Stammdaten.')}
      </div>
      {gesperrt && (
        <div style={{ padding: '10px 12px', background: C.gray100, borderLeft: '4px solid ' + C.gray500, borderRadius: 6, marginBottom: 12, fontSize: 15, color: C.navy }}>
          🔒 {t('Diese Wartung ist abgeschlossen. Änderungen sind nur noch durch das Büro möglich.')}
        </div>
      )}
      <fieldset disabled={!!gesperrt} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
        <EditableStammdaten modul={modul} station={station} config={config} wandler={wandler}
          onStationReload={onStationReload} stationFehler={stationFehler} onPruefpunktChange={null} protokollOffen={!gesperrt}/>
      </fieldset>
    </div>
  );
}

// === NSHV: Bezeichnung der Verteilung (Anna-Lena 08.10.2026, Migration 76) ====
// Kopf in jedem NSHV-Kapitel: Name der Verteilung ("NSHV", "VT 2 Keller"),
// umbenennen und - bei einer weiteren Verteilung, solange nichts beantwortet
// ist - wieder entfernen.
function VerteilungKopf({ modul, stationId, gesperrt, onGeaendert }) {
  const [bearbeiten, setBearbeiten] = useState(false);
  const [name, setName] = useState(modul.bezugs_label || '');
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState(null);
  useEffect(() => { if (!bearbeiten) setName(modul.bezugs_label || ''); }, [modul.bezugs_label, bearbeiten]);
  const weitere = modul.bezugs_typ === 'verteilung';

  const speichern = async () => {
    setLaeuft(true); setFehler(null);
    try {
      await verteilungUmbenennen(modul, stationId, name);
      setBearbeiten(false);
      if (onGeaendert) await onGeaendert();
    } catch (e) {
      setFehler(t('Nicht gespeichert: {fehler}', { fehler: e.message || t('kein Netz?') }));
    } finally { setLaeuft(false); }
  };
  const entfernen = async () => {
    if (!window.confirm(t('Verteilung „{name}“ mit ihren Prüfpunkten entfernen?', { name: modul.bezugs_label || '' }))) return;
    setLaeuft(true); setFehler(null);
    try {
      await verteilungEntfernen(modul, stationId);
      if (onGeaendert) await onGeaendert({ entfernt: modul.id });
    } catch (e) {
      setFehler(e.message || t('Konnte nicht entfernt werden.'));
    } finally { setLaeuft(false); }
  };

  const knopf = { minHeight: 44, padding: '8px 14px', borderRadius: 8, border: '1.5px solid ' + C.gray200,
                  background: C.white, color: C.navy, fontWeight: 700, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' };
  return (
    <div style={{ padding: '10px 12px', background: C.gray100, borderLeft: '4px solid ' + C.navy, borderRadius: 6, marginBottom: 14 }}>
      {!bearbeiten ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 180 }}>
            <div style={{ fontSize: 13, color: C.gray500 }}>{weitere ? t('Weitere Verteilung') : t('Verteilung')}</div>
            <div style={{ fontSize: 16, fontWeight: 800, color: C.navy }}>
              {modul.bezugs_label || <span style={{ fontWeight: 600, color: C.gray500, fontStyle: 'italic' }}>{t('ohne Bezeichnung')}</span>}
            </div>
          </div>
          {!gesperrt && <button type='button' style={knopf} onClick={() => setBearbeiten(true)}>{t('Bezeichnung ändern')}</button>}
          {!gesperrt && weitere && <button type='button' style={{ ...knopf, color: C.gray700 }} disabled={laeuft} onClick={entfernen}>{t('entfernen')}</button>}
        </div>
      ) : (
        <div>
          <div style={{ fontSize: 13, color: C.gray500, marginBottom: 6 }}>{t('Bezeichnung der Verteilung, z. B. „NSHV“ oder „VT 2 Keller“')}</div>
          <TextInput value={name} onChange={setName}/>
          <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
            <button type='button' onClick={speichern} disabled={laeuft}
              style={{ ...knopf, border: 'none', background: laeuft ? C.gray300 : C.navy, color: C.white }}>
              {laeuft ? t('speichert...') : t('Übernehmen')}
            </button>
            <button type='button' style={knopf} disabled={laeuft} onClick={() => { setBearbeiten(false); setFehler(null); }}>{t('Abbrechen')}</button>
          </div>
        </div>
      )}
      {fehler && <div style={{ marginTop: 8, padding: '8px 10px', background: C.redBg, color: C.red, borderRadius: 6, fontSize: 14, fontWeight: 700 }}>{fehler}</div>}
    </div>
  );
}

// "+ weitere Verteilung": neues NSHV-Kapitel mit allen Pruefpunkten, direkt
// hinter der NSHV. Bleibt in den Stammdaten - naechstes Jahr ist es wieder da.
function VerteilungNachtragen({ protokollId, stationId, onFertig }) {
  const [offen, setOffen] = useState(false);
  const [name, setName] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState(null);
  if (!protokollId || !stationId) return null;

  const anlegen = async () => {
    setLaeuft(true); setFehler(null);
    try {
      const kapitel = await verteilungNachtragen(protokollId, stationId, name);
      setOffen(false); setName('');
      if (onFertig) onFertig(kapitel);
    } catch (e) {
      setFehler(t('Die Verteilung konnte nicht angelegt werden: {fehler}', { fehler: e.message || t('kein Netz?') }));
    } finally { setLaeuft(false); }
  };

  if (!offen) {
    return (
      <button type='button' onClick={() => setOffen(true)}
        style={{ width: '100%', minHeight: 52, padding: '12px 16px', marginTop: 12, borderRadius: 10,
                 border: '2px dashed ' + C.gray300, background: C.white, color: C.navy,
                 fontWeight: 700, fontSize: 15, cursor: 'pointer', fontFamily: 'inherit' }}>
        {t('+ weitere Verteilung (z. B. VT 2 Keller)')}
      </button>
    );
  }
  return (
    <div style={{ marginTop: 12, padding: 14, background: C.white, border: '2px solid ' + C.navy, borderRadius: 10 }}>
      <div style={{ fontWeight: 800, color: C.navy, fontSize: 15, marginBottom: 4 }}>{t('Weitere Verteilung')}</div>
      <div style={{ fontSize: 14, color: C.gray500, marginBottom: 10, lineHeight: 1.4 }}>
        {t('Bekommt ein eigenes Kapitel mit allen NSHV-Prüfpunkten und bleibt in den Stammdaten der Station – bei der nächsten Wartung ist sie wieder dabei.')}
      </div>
      <TextInput value={name} onChange={setName} placeholder={t('Bezeichnung, z. B. "VT 2 Keller"')}/>
      {fehler && <div style={{ marginTop: 8, padding: '8px 10px', background: C.redBg, color: C.red, borderRadius: 6, fontSize: 14, fontWeight: 700 }}>{fehler}</div>}
      <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
        <button type='button' onClick={anlegen} disabled={laeuft}
          style={{ minHeight: 48, padding: '11px 20px', borderRadius: 8, border: 'none',
                   background: laeuft ? C.gray300 : C.navy, color: C.white, fontWeight: 800, fontSize: 15,
                   cursor: laeuft ? 'wait' : 'pointer', fontFamily: 'inherit' }}>
          {laeuft ? t('wird angelegt …') : t('Verteilung anlegen')}
        </button>
        <button type='button' onClick={() => { setOffen(false); setFehler(null); }} disabled={laeuft}
          style={{ minHeight: 48, padding: '11px 18px', borderRadius: 8, border: '1.5px solid ' + C.gray200,
                   background: C.white, color: C.gray700, fontWeight: 700, fontSize: 15, cursor: 'pointer', fontFamily: 'inherit' }}>
          {t('Abbrechen')}
        </button>
      </div>
    </div>
  );
}

// kompakt: kleinere Knoepfe nebeneinander (Gruppen wie "Tueren und Schloesser").
// teil: 'knoepfe' = nur die Antwortknoepfe, 'rest' = nur Gruende und Zusatzfelder
// (die kompakte Zeile zeigt die Knoepfe neben dem Stichwort, den Rest darunter).
// ohneKnoepfe: nur Zusatzfelder, die Bewertung kommt automatisch (03.5 beim Monteur).
function PruefpunktInput({ pp, onChange, kompakt = false, teil = null, ohneKnoepfe = false }) {
  const opts = pp.auswahl_optionen;
  if (pp.typ === 'auswahl' && Array.isArray(opts) && opts.length > 0) {
    // Fragen, deren Antworten alle "eigene" sind (05.1.1 Ausfuehrung der Wanne:
    // Blech / Kunststoff / sonstiges / Anstrich), haben keine i.O.-/n.i.O.-Knoepfe -
    // dann sind alle Antworten normale, neutrale Knoepfe statt "Gruende".
    const nurEigene = opts.every(o => !isPrimary(o));
    const primary = nurEigene ? opts : opts.filter(isPrimary);
    const modifiers = nurEigene ? [] : opts.filter(o => !isPrimary(o));
    const currentModifiers = Array.isArray(pp.wert_modifiers) ? pp.wert_modifiers : [];
    const isNotOk = pp.in_ordnung === false;
    const nurBauart = NEIN_OHNE_MANGEL.includes(String(pp.pos_nummer || '').trim());

    const handlePrimary = (opt) => {
      if (pp.wert_auswahl === opt) {
        onChange({ wert_auswahl: null, in_ordnung: null, wert_modifiers: [] });
        return;
      }
      let in_ordnung = null;
      if (isPrimaryPositive(opt)) in_ordnung = true;
      else if (isPrimaryNegative(opt)) in_ordnung = false;
      if (pp.logik_invertiert && in_ordnung !== null) in_ordnung = !in_ordnung;
      if (nurBauart && in_ordnung === false) in_ordnung = null;
      // Gruende ("fehlt", "beschaedigt") gehoeren nur zu n.i.O. - vorher blieben
      // sie bei "n. benoetigt" stehen und das PDF zeigte "n. benötigt · fehlt".
      const newModifiers = in_ordnung === false ? currentModifiers : [];
      onChange({ wert_auswahl: opt, in_ordnung, wert_modifiers: newModifiers });
    };

    const toggleModifier = (m) => {
      const has = currentModifiers.includes(m);
      const newList = has ? currentModifiers.filter(x => x !== m) : [...currentModifiers, m];
      onChange({ wert_modifiers: newList });
    };

    const zeigeKnoepfe = teil !== 'rest' && !ohneKnoepfe;
    const zeigeRest = teil !== 'knoepfe';
    // In der kompakten Zeile: "i.O. – Schluesselkasten" nur als "Schluesselkasten".
    const knopfText = (opt) => (kompakt && /^i\.?\s?O\.?\s*[–-]\s*\S/.test(opt)
      ? optionText(opt).replace(/^[^–-]*[–-]\s*/, '') : optionText(opt));
    return (
      <div>
        {zeigeKnoepfe && (<>
        {/* Antwortknoepfe fuer den Daumen: 56 px hoch, ueber die volle Breite,
            zwei pro Zeile. Vorher waren sie rund 30 px hoch - mit Arbeits-
            handschuhen kaum zu treffen. Reihenfolge bleibt wie im Papier-
            protokoll (i.O. links), damit die Monteure blind tippen koennen. */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: kompakt ? 6 : 10, marginBottom: kompakt ? 0 : 8,
                      justifyContent: kompakt ? 'flex-end' : 'flex-start' }}>
          {primary.map(opt => {
            const selected = pp.wert_auswahl === opt;
            const effectivePos = pp.logik_invertiert ? isPrimaryNegative(opt) : isPrimaryPositive(opt);
            const effectiveNeg = !nurBauart && (pp.logik_invertiert ? isPrimaryPositive(opt) : isPrimaryNegative(opt));
            let bg = C.white, color = C.gray700, border = C.gray200;
            if (selected) {
              if (effectivePos)      { bg = C.green; color = C.white; border = C.green; }
              else if (effectiveNeg) { bg = C.red;   color = C.white; border = C.red; }
              else                    { bg = C.navy;  color = C.yellow; border = C.navy; }
            }
            return (
              <button key={opt} type='button' onClick={() => handlePrimary(opt)}
                style={{ flex: kompakt ? '0 0 auto' : '1 1 42%', minHeight: kompakt ? 46 : 56,
                         padding: kompakt ? '8px 10px' : '10px 14px', borderRadius: 9, background: bg, color,
                         border: '2px solid ' + border, cursor: 'pointer', fontWeight: 800, fontSize: kompakt ? 14 : 15,
                         touchAction: 'manipulation' }}>{knopfText(opt)}</button>
            );
          })}
        </div>
        </>)}
        {zeigeRest && isNotOk && modifiers.length > 0 && (
          <div style={{ paddingLeft: 12, borderLeft: '3px solid ' + C.red, marginBottom: 6, marginTop: kompakt ? 8 : 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.red, marginBottom: 5 }}>{t('Warum nicht in Ordnung? (Mehrfachauswahl):')}</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {modifiers.map(m => {
                const checked = currentModifiers.includes(m);
                return (
                  <button key={m} type='button' onClick={() => toggleModifier(m)}
                    style={{ minHeight: 46, padding: '8px 14px', borderRadius: 8, background: checked ? C.red : C.white, color: checked ? C.white : C.gray700, border: '2px solid ' + (checked ? C.red : C.gray200), cursor: 'pointer', fontWeight: 700, fontSize: 14, touchAction: 'manipulation' }}>
                    {checked ? '✓ ' : ''}{optionText(m)}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        {zeigeRest && <ZusatzFelder pp={pp} onChange={onChange}/>}
      </div>
    );
  }
  if (pp.typ === 'messwert') {
    return (
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <NumberInput value={pp.wert_zahl} onChange={v => onChange({ wert_zahl: v })} step={0.01}/>
        {pp.einheit && <span style={{ fontSize: 15, color: C.gray500 }}>{pp.einheit}</span>}
      </div>
    );
  }
  if (pp.typ === 'boolean') {
    return (
      <div style={{ display: 'flex', gap: 10 }}>
        <button type='button' onClick={() => onChange({ wert_bool: true, in_ordnung: true })}
          style={{ flex: 1, minHeight: 56, padding: '10px 14px', borderRadius: 9, background: pp.wert_bool === true ? C.green : C.white, color: pp.wert_bool === true ? C.white : C.gray700, border: '2px solid ' + (pp.wert_bool === true ? C.green : C.gray200), cursor: 'pointer', fontWeight: 800, fontSize: 15, touchAction: 'manipulation' }}>{t('Ja')}</button>
        <button type='button' onClick={() => onChange({ wert_bool: false, in_ordnung: false })}
          style={{ flex: 1, minHeight: 56, padding: '10px 14px', borderRadius: 9, background: pp.wert_bool === false ? C.red : C.white, color: pp.wert_bool === false ? C.white : C.gray700, border: '2px solid ' + (pp.wert_bool === false ? C.red : C.gray200), cursor: 'pointer', fontWeight: 800, fontSize: 15, touchAction: 'manipulation' }}>{t('Nein')}</button>
      </div>
    );
  }
  return <TextInput value={pp.wert_text} onChange={v => onChange({ wert_text: v })} placeholder={t('Eingabe...')}/>;
}

// 15.05.: Auto-Hinweise fuer spezielle Pruefpunkte.
// #35 - Gasdruck bei Luftanlage nicht zutreffend.
// #36 - Lebenszyklus-Empfehlung anhand Bauteil-Alter.
function PruefpunktAutoHinweis({ pp, config, msFelder, trafos, geschwister }) {
  if (!pp || !pp.pos_nummer) return null;
  const pos = String(pp.pos_nummer).trim();

  // Ölwanne: "nein" bei Blechwanne UND bei Beschichtung ist kein Mangel je
  // Frage - zusammen heisst es aber: gar keine Wanne. Dann gehoert der Mangel
  // an 05.1. Nur Hinweis, nichts wird automatisch umgestellt.
  if (pos === '05.2.1' && pp.wert_auswahl === 'nein') {
    const blech = (geschwister || []).find(x => String(x.pos_nummer || '').trim() === '05.1.1');
    if (blech && blech.wert_auswahl === 'nein') {
      return (
        <div style={{ padding: '8px 10px', background: C.orangeBg, color: C.navy, borderRadius: 6, fontSize: 14, marginBottom: 6, borderLeft: '3px solid ' + C.orange }}>
          ⚠️ <strong>{t('Weder Blechwanne noch Beschichtung.')}</strong> {t('Dann ist keine Ölauffangwanne vorhanden – bitte 05.1 auf „nein" setzen (Mangel).')}
        </div>
      );
    }
  }

  // #35 (Gasdruck bei Luftanlage) steht seit v54 in lib/anlagenRegeln.js -
  // dort zusammen mit allen anderen Vorschlaegen nach Anlagenart.

  // #36: Lebenszyklus-Empfehlung (>25 Jahre = rot)
  if (pos === '02.12') {
    // Gleiche Rechnung wie im PDF (lebensdauerBefund): nur die MS-Felder,
    // Grenze ab LEBENSDAUER_JAHRE.ms_anlage Jahren. Vorher zaehlten auch Trafo und
    // Gebaeude mit, und genau 25 Jahre war hier "nein", im PDF "ueberschritten".
    const jahrJetzt = new Date().getFullYear();
    const baujahre = (msFelder || []).map(f => Number(f.herstellungsjahr)).filter(j => j >= 1900 && j <= jahrJetzt);
    if (baujahre.length === 0) return null;
    const aeltestes = Math.min(...baujahre);
    const alter = new Date().getFullYear() - aeltestes;
    const ms = lebensdauerBefund(null, [], msFelder).posten.find(p => p.was === 'ms_anlage');
    if (ms && ms.stufe === 'ueberschritten') {
      return (
        <div style={{ padding: '8px 10px', background: C.redBg, color: C.red, borderRadius: 6, fontSize: 14, marginBottom: 6, borderLeft: '3px solid ' + C.red }}>
          ⚠️ <strong>{t('Lebenszyklus erreicht:')}</strong> {t('Ältestes MS-Feld von {jahr} ist {alter} Jahre alt (ab {grenze} Jahren erreicht).', { jahr: aeltestes, alter, grenze: LEBENSDAUER_JAHRE.ms_anlage })} {t('Empfehlung:')} <strong>{t('ja, Lebenszyklus erreicht')}</strong> - {t('Mangel.')}
        </div>
      );
    }
    return (
      <div style={{ padding: '8px 10px', background: C.greenBg, color: C.green, borderRadius: 6, fontSize: 14, marginBottom: 6, borderLeft: '3px solid ' + C.green }}>
        ✅ <strong>{t('Lebenszyklus nicht erreicht:')}</strong> {t('Ältestes MS-Feld von {jahr} ist {alter} Jahre alt (unter {grenze} Jahren).', { jahr: aeltestes, alter, grenze: LEBENSDAUER_JAHRE.ms_anlage })} {t('Empfehlung:')} <strong>{t('nein')}</strong>.
      </div>
    );
  }
  return null;
}

function BemerkungInput({ value, onChange }) {
  const [local, setLocal] = useState(value || '');
  const timer = useRef(null);
  // Noch nicht abgegebener Text. Wird beim Speichern-Knopf, beim Verlassen
  // des Feldes, beim Wechsel in den Hintergrund und beim Abbau SOFORT
  // abgegeben - vorher ging getippter Text in den letzten 0,5 s verloren.
  const offen = useRef(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => { if (offen.current === null) setLocal(value || ''); }, [value]);

  const abgeben = () => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    if (offen.current !== null) {
      const v = offen.current;
      offen.current = null;
      onChangeRef.current(v);
    }
  };

  useEffect(() => {
    window.addEventListener('etb-jetzt-speichern', abgeben);
    return () => { window.removeEventListener('etb-jetzt-speichern', abgeben); abgeben(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handle = (newVal) => {
    setLocal(newVal);
    offen.current = newVal;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(abgeben, 500);
  };

  return (
    <input type='text' value={local} onChange={e => handle(e.target.value)} onBlur={abgeben}
      placeholder={t('Bemerkung (optional)')}
      // 16 px, sonst zoomt Safari auf dem iPhone beim Antippen hinein.
      // Hoehe passend zu den grossen Antwortknoepfen darueber.
      style={{ width: '100%', padding: '12px 12px', borderRadius: 8, border: '1.5px solid ' + C.gray200, fontSize: 16, minHeight: 48, outline: 'none', boxSizing: 'border-box', background: C.white }}/>
  );
}

// === Vorschlag nach Anlagenart ================================================
// Die App schlaegt vor, der Monteur entscheidet: Ein Tipp uebernimmt z. B.
// "n. benötigt". Nichts wird automatisch gesetzt oder ausgeblendet.
function AnlagenVorschlag({ regel, pp, onUebernehmen }) {
  if (!regel) return null;
  const opt = regel.art === 'nicht_erforderlich' ? optionNichtErforderlich(pp) : null;
  const schonGesetzt = opt && pp.wert_auswahl === opt;
  const stil = regel.art === 'erforderlich'
    ? { bg: C.blueBg, rand: C.navy, titel: t('Laut Anlage erforderlich') }
    : regel.art === 'nicht_erforderlich'
      ? { bg: C.greenBg, rand: C.green, titel: t('Laut Anlage voraussichtlich nicht erforderlich') }
      : { bg: C.yellowBg, rand: C.yellow, titel: t('Hinweis zur Anlage') };
  return (
    <div style={{ padding: '8px 10px', background: stil.bg, borderLeft: '3px solid ' + stil.rand, borderRadius: 6,
                  fontSize: 14, color: C.navy, marginBottom: 6, lineHeight: 1.4 }}>
      <strong>{stil.titel}:</strong> {t(regel.text)}
      {opt && !schonGesetzt && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
          <button type='button' onClick={() => onUebernehmen(opt)}
            style={{ minHeight: 40, padding: '6px 12px', borderRadius: 7, border: '1.5px solid ' + C.green, background: C.white,
                     color: C.green, fontWeight: 800, fontSize: 14, cursor: 'pointer', touchAction: 'manipulation' }}>
            {t('„{option}" übernehmen', { option: optionText(opt.replace(/\s+/g, ' ')) })}
          </button>
          <span style={{ fontSize: 12, color: C.gray500 }}>{t('Vorschlag – bitte vor Ort selbst prüfen')}</span>
        </div>
      )}
    </div>
  );
}

// === "i"-Knopf je Pruefpunkt ==================================================
// Anna-Lena 13.09.2026: "kleine i's, auf die ich druecken kann, wenn ich wissen
// will, was dahinter steckt - welche Norm, worauf ich achten muss - so dass
// theoretisch ein Laie das Protokoll ausfuellen koennte." Texte stehen in
// lib/pruefpunktInfo.js (zum Gegenlesen durch das Buero).
function PruefpunktInfo({ modulCode, pp }) {
  const [offen, setOffen] = useState(false);
  const info = infoFuer(modulCode, pp?.pos_nummer);
  if (!info) return null;
  return (
    <div style={{ marginTop: 6 }}>
      <button type='button' onClick={() => setOffen(o => !o)} aria-expanded={offen}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minHeight: 36, padding: '4px 10px 4px 4px',
                 borderRadius: 18, border: '1.5px solid ' + (offen ? C.navy : C.gray200), background: offen ? C.navy : C.white,
                 color: offen ? C.white : C.navy, fontSize: 14, fontWeight: 700, cursor: 'pointer', touchAction: 'manipulation' }}>
        <span style={{ width: 24, height: 24, borderRadius: 12, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                       background: offen ? C.white : C.navy, color: offen ? C.navy : C.white, fontSize: 15, fontWeight: 800,
                       fontFamily: 'Georgia, serif' }}>i</span>
        {offen ? t('Erklärung schließen') : t('Was prüfen? Worauf achten?')}
      </button>
      {offen && (
        <div style={{ marginTop: 8, padding: '12px 14px', background: C.blueBg, borderLeft: '4px solid ' + C.navy,
                      borderRadius: 7, fontSize: 15, color: C.navy, lineHeight: 1.5, fontWeight: 400 }}>
          {info.was && <div style={{ marginBottom: 8 }}><strong>{t('Worum geht es:')}</strong> {info.was}</div>}
          {Array.isArray(info.achten) && info.achten.length > 0 && (
            <div style={{ marginBottom: 8 }}>
              <strong>{t('Worauf achten:')}</strong>
              <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
                {info.achten.map((a, i) => <li key={i} style={{ marginBottom: 2 }}>{a}</li>)}
              </ul>
            </div>
          )}
          {info.nio && <div style={{ marginBottom: 8 }}><strong>{t('n.i.O., wenn:')}</strong> {info.nio}</div>}
          {info.norm && <div style={{ fontSize: 14, color: C.gray700 }}><strong>{t('Grundlage:')}</strong> {info.norm}</div>}
        </div>
      )}
    </div>
  );
}

function ModulPanel({ modul, protokollId, stationId, station, config, vnbName, msFelder, trafos, wandler, onUpdateLocal, onStationReload, stationFehler, letzterStand, onErsteEingabe, zielPunktId, onZielErreicht, gesperrt, abgeschlossenBuero, onKapitelGeaendert }) {
  const { user, isBuero } = useAuth();
  const [savingIds, setSavingIds] = useState(new Set());
  const [error, setError] = useState(null);
  // Wie viele Fotos haengen an welchem Pruefpunkt? Wird von den Foto-Knoepfen
  // gemeldet und fuer den Beleg-Hinweis bei "n.i.O." gebraucht.
  const [fotoAnzahl, setFotoAnzahl] = useState({});
  const meldeFotoAnzahl = useCallback((ppId, n) => {
    setFotoAnzahl(alt => (alt[ppId] === n ? alt : { ...alt, [ppId]: n }));
  }, []);
  // Fuer den Sprung zum naechsten offenen Punkt
  const punktRefs = useRef({});
  // Kompakte Gruppen: bei welchen Zeilen sind Bemerkung/Foto aufgeklappt?
  const [kompaktOffen, setKompaktOffen] = useState({});
  // Sprung aus der Liste "Offene Punkte" (anderes Kapitel): warten, bis der
  // Punkt gezeichnet ist, dann hinscrollen und kurz hervorheben.
  useEffect(() => {
    if (!zielPunktId) return undefined;
    const zeit = setTimeout(() => {
      const el = punktRefs.current[zielPunktId];
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const alt = el.style.boxShadow;
        el.style.boxShadow = '0 0 0 3px ' + C.yellow;
        setTimeout(() => { el.style.boxShadow = alt; }, 1800);
      }
      if (onZielErreicht) onZielErreicht();
    }, 250);
    return () => clearTimeout(zeit);
  }, [zielPunktId, modul?.id, onZielErreicht]);

  const handleChange = async (pp, changes) => {
    // Abgeschlossen: Monteure aendern nichts mehr (so steht es auch in der
    // Rueckfrage beim Abschliessen). Doppelt abgesichert - die Felder sind
    // ohnehin gesperrt.
    if (gesperrt) return;
    // 03.5 Absicherung Ueberspannungsschutz (Migration 69): aus Ist/Soll die
    // Bewertung ableiten - fuer Monteur UND Buero, auch im abgeschlossenen Protokoll.
    const istUesp = modul?.modul_vorlagen?.code === UESP_MODUL && String(pp.pos_nummer || '').trim() === UESP_POS;
    if (istUesp && changes.zusatz_werte) {
      const aktuell = letzterStand?.current?.[pp.id] || pp;
      const bewertung = uespBewertung(changes.zusatz_werte, aktuell.bemerkung);
      if (bewertung) changes = { ...changes, ...bewertung };
      const w = changes.zusatz_werte;
      const leer = (v) => v == null || v === '';
      // Soll unbekannt: in der Datenbank nachsehen und, wenn vorhanden, eintragen.
      if (leer(w.soll_a) && w.hersteller && w.typ) {
        sollNachschlagen(w.hersteller, w.typ).then(soll => {
          if (soll == null) return;
          const jetzt = letzterStand?.current?.[pp.id] || pp;
          const neu = { ...(jetzt.zusatz_werte || {}), soll_a: soll };
          if (leer((jetzt.zusatz_werte || {}).soll_a)) handleChange(pp, { zusatz_werte: neu });
        }).catch(() => {});
      }
      // Buero traegt den Soll-Wert ein: fuer diesen Typ merken.
      if (isBuero && !leer(w.soll_a) && w.hersteller && w.typ) {
        sollMerken(w.hersteller, w.typ, w.soll_a).catch(() => {});
      }
    }
    // Auf dem NEUESTEN Stand aufsetzen, nicht auf dem, den die aufrufende
    // Stelle kennt. Die Bemerkung speichert 0,5 s verzoegert mit dem Stand von
    // damals - tippte der Monteur in der Zeit n.i.O., wurde die Antwort mit
    // dem alten Stand wieder ueberschrieben.
    const basis = letzterStand?.current?.[pp.id] || pp;
    const merged = { ...basis, ...changes };
    if (letzterStand?.current) letzterStand.current[pp.id] = merged;
    onUpdateLocal(merged);
    if (onErsteEingabe) onErsteEingabe();
    setSavingIds(s => new Set([...s, pp.id]));
    try {
      // Gesendet werden nur die geaenderten Felder (siehe lib/warteschlange.js).
      await savePruefpunkt(merged, changes, protokollId, stationId, user?.id);
      // Eine Fehlermeldung zu DIESEM Punkt ist damit erledigt - die zu einem
      // anderen Punkt bleibt stehen (vorher loeschte jeder Tipp jede Meldung).
      setError(e => (e && e.ppId === pp.id ? null : e));
    } catch (e) {
      setError({ ppId: pp.id, text: t('Pos. {pos} wurde NICHT gespeichert: {fehler}. Bitte im Büro Bescheid geben.',
        { pos: pp.pos_nummer || '?', fehler: e.message || t('Fehler beim Speichern') }) });
      console.error('[Save] Fehler:', e);
    } finally {
      setSavingIds(s => {
        const c = new Set(s); c.delete(pp.id); return c;
      });
    }
  };

  // Nur die Punkte, die unter den aktuellen Antworten ueberhaupt Sinn ergeben.
  // Ausgeblendete zaehlen auch nicht mehr in "x von y ausgefuellt" mit.
  const alle = modul.pruefpunkte || [];
  // Lage der Anlage (SF6/Luft, Messfeld, Trafo gesteckt ...) - auch fuer die Sichtbarkeit (05.18/05.19).
  const lage = anlagenLage(config, msFelder, trafos);
  const sichtbare = alle.filter(pp => istSichtbar(pp, alle, trafos, { config, vnbName, lage, trafo: trafoDesKapitels(modul, trafos) }));

  // Vorschlaege nach Anlagenart (SF6/Luft, Messfeld, Trafo gesteckt/offen ...)
  const modulCode = modul?.modul_vorlagen?.code;
  // Angezeigte Nummer: in Kapitel 02 fortlaufend (pos_nummer bleibt intern).
  const nummern = anzeigeNummern(modulCode, sichtbare);
  const kapitelTrafo = modulCode === '04-TRAFO-SICHT' ? (trafos || []).find(t => t.id === modul?.bezugs_id) : null;
  const hinweiseOben = kapitelHinweise(modulCode, lage).map(h => t(h));

  const grouped = {};
  for (const pp of sichtbare) {
    const k = pp.kategorie || '_default';
    if (!grouped[k]) grouped[k] = [];
    grouped[k].push(pp);
  }
  const categories = Object.keys(grouped);

  const filledCount = sichtbare.filter(pp =>
    pp.wert_auswahl != null || pp.wert_zahl != null || pp.wert_bool != null || (pp.wert_text && pp.wert_text.length > 0)
  ).length;
  const totalCount = sichtbare.length;

  // Springt zum ersten noch unbeantworteten Punkt dieses Moduls und hebt ihn
  // kurz hervor, damit klar ist, wo man gelandet ist.
  const springeZumNaechstenOffenen = () => {
    // Gleiche Regel wie der Zaehler (istBeantwortet) - vorher sprang der Knopf
    // zu einem Punkt mit nur Leerzeichen, den der Zaehler als fertig zaehlte.
    const offen = sichtbare.find(pp => !istBeantwortet(pp));
    if (!offen) return;
    const el = punktRefs.current[offen.id];
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const alt = el.style.boxShadow;
    el.style.boxShadow = '0 0 0 3px ' + C.yellow;
    setTimeout(() => { el.style.boxShadow = alt; }, 1600);
  };

  return (
    <div style={{ background: C.white, borderRadius: 10, padding: 'clamp(14px, 3vw, 22px)', boxShadow: '0 1px 6px rgba(10,22,40,0.06)' }}>
      <div style={{ marginBottom: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontWeight: 800, color: C.navy, fontSize: 16 }}>
            {modulName(modulCode, modul.modul_vorlagen?.name) || t('Modul')}
            {modul.bezugs_label && <span style={{ marginLeft: 8, fontSize: 14, fontWeight: 600, color: C.gray500 }}>- {modul.bezugs_label}</span>}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ background: filledCount === totalCount ? C.greenBg : C.gray100, color: filledCount === totalCount ? C.green : C.gray700, padding: '8px 14px', borderRadius: 7, fontSize: 15, fontWeight: 700 }}>
            {t('{fertig} / {gesamt} ausgefüllt', { fertig: filledCount, gesamt: totalCount })}
          </div>
          {/* Bei rund 140 Pruefpunkten ist das Suchen nach dem, was noch fehlt,
              die eigentliche Arbeit. Der Knopf springt direkt dorthin. */}
          {filledCount < totalCount && (
            <button type='button' onClick={springeZumNaechstenOffenen}
              style={{ minHeight: 44, padding: '9px 16px', borderRadius: 8, background: C.navy, color: C.yellow,
                       border: 'none', cursor: 'pointer', fontWeight: 800, fontSize: 15, touchAction: 'manipulation' }}>
              {t('↓ Nächster offener Punkt')}
            </button>
          )}
        </div>
      </div>

      {/* Fortschritt auf einen Blick */}
      <div style={{ height: 8, background: C.gray100, borderRadius: 999, overflow: 'hidden', marginBottom: 16 }}>
        <div style={{ width: (totalCount > 0 ? Math.round(filledCount / totalCount * 100) : 0) + '%', height: '100%',
                      background: filledCount === totalCount ? C.green : C.navy, transition: 'width .25s ease' }}/>
      </div>

      {modulCode === '01-STATIONSKOERPER' && <KundenAbsprachen stationId={stationId}/>}
      {modulCode === '03-NSHV' && <VerteilungKopf modul={modul} stationId={stationId} gesperrt={gesperrt} onGeaendert={onKapitelGeaendert}/>}
      {hinweiseOben.map((h, i) => (
        <div key={i} style={{ padding: '10px 12px', background: C.yellowBg, borderLeft: '4px solid ' + C.yellow, borderRadius: 6, marginBottom: 10, fontSize: 14, color: C.navy }}>
          {h}
        </div>
      ))}
      {gesperrt && (
        <div style={{ padding: '10px 12px', background: C.gray100, borderLeft: '4px solid ' + C.gray500, borderRadius: 6, marginBottom: 12, fontSize: 15, color: C.navy }}>
          🔒 {t('Diese Wartung ist abgeschlossen. Änderungen sind nur noch durch das Büro möglich.')}
        </div>
      )}
      {abgeschlossenBuero && (
        <div style={{ padding: '10px 12px', background: C.orangeBg, borderLeft: '4px solid ' + C.orange, borderRadius: 6, marginBottom: 12, fontSize: 15, color: C.navy }}>
          {t('Abgeschlossene Wartung – Änderungen des Büros werden sofort übernommen. Ein bereits verschicktes PDF danach neu erstellen.')}
        </div>
      )}
      {/* fieldset disabled sperrt alle Eingaben und Knoepfe darin auf einmal. */}
      <fieldset disabled={!!gesperrt} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
      <EditableStammdaten modul={modul} station={station} config={config} msFelder={msFelder} trafos={trafos} wandler={wandler} onStationReload={onStationReload} stationFehler={stationFehler} onPruefpunktChange={gesperrt ? null : handleChange}
        protokollOffen={!gesperrt && !abgeschlossenBuero}/>

      {error && <div style={{ padding: 10, background: C.redBg, color: C.red, borderRadius: 7, marginBottom: 12, fontSize: 14 }}>! {error.text}</div>}

      {categories.map(cat => KOMPAKT_KATEGORIEN.has(cat) ? (
        // Kompakte Gruppe (Anna-Lena 22.09.2026, "Tueren und Schloesser"): je Punkt
        // EINE Zeile mit Stichwort und Knoepfen, oben das Gesamtergebnis der Gruppe.
        // Jeder Punkt bleibt ein eigener Pruefpunkt (eigener Mangel, eigenes PDF-Feld).
        <div key={cat} style={{ marginBottom: 18 }}>
          {(() => {
            const liste = grouped[cat];
            const offen = liste.filter(pp => !istBeantwortet(pp)).length;
            const nio = liste.filter(pp => pp.in_ordnung === false).length;
            const [text, farbe, hinter] = nio > 0 ? [t('n.i.O.') + ' (' + nio + ')', C.white, C.red]
              : offen > 0 ? [t('{n} offen', { n: offen }), C.gray700, C.gray100]
                : [t('i.O.'), C.white, C.green];
            return (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10,
                            paddingTop: 12, marginBottom: 8, borderTop: '2px solid ' + C.gray100 }}>
                <div style={{ fontWeight: 800, color: C.navy, fontSize: 15, textTransform: 'uppercase', letterSpacing: 0.5 }}>{kategorieText(cat)}</div>
                <span style={{ background: hinter, color: farbe, fontWeight: 800, fontSize: 14, padding: '5px 12px', borderRadius: 999 }}>{text}</span>
              </div>
            );
          })()}
          <div style={{ background: C.gray100, borderRadius: 8, overflow: 'hidden' }}>
            {grouped[cat].map((pp, i) => {
              const istNichtOk = pp.in_ordnung === false;
              const belegFehlt = istNichtOk
                && !BEGRUENDUNG_KOMMT_VON_DER_APP.includes(pp.pos_nummer)
                && !(Array.isArray(pp.wert_modifiers) && pp.wert_modifiers.length > 0)
                && !(pp.bemerkung && pp.bemerkung.trim() !== '')
                && !(fotoAnzahl[pp.id] > 0);
              const details = istNichtOk || kompaktOffen[pp.id] || (pp.bemerkung && pp.bemerkung.trim() !== '') || fotoAnzahl[pp.id] > 0;
              return (
                <div key={pp.id} ref={el => { punktRefs.current[pp.id] = el; }}
                  style={{ padding: '7px 10px', borderTop: i === 0 ? 'none' : '1px solid ' + C.gray200,
                           background: istNichtOk ? C.redBg : 'transparent', borderLeft: '4px solid ' + (istNichtOk ? C.red : 'transparent'),
                           scrollMarginTop: 90 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <div lang='de' style={{ flex: '1 1 100px', minWidth: 0, fontWeight: 700, color: C.navy, fontSize: 15, lineHeight: 1.25, hyphens: 'auto', WebkitHyphens: 'auto', overflowWrap: 'anywhere' }}>
                      {pp.pos_nummer && <span style={{ color: C.gray500, marginRight: 5, fontSize: 13 }}>{anzeigeNummer(pp, nummern)}</span>}
                      {ppText(modulCode, pp, 'bezeichnung')}
                      {savingIds.has(pp.id) && <span style={{ marginLeft: 8, fontSize: 12, color: C.gray500, fontWeight: 400 }}>{t('speichert...')}</span>}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}>
                      <PruefpunktInput pp={pp} onChange={(c) => handleChange(pp, c)} kompakt teil='knoepfe'/>
                      {!details && (
                        <button type='button' aria-label={t('Bemerkung / Foto')} title={t('Bemerkung / Foto')}
                          onClick={() => setKompaktOffen(o => ({ ...o, [pp.id]: true }))}
                          style={{ minWidth: 38, minHeight: 46, borderRadius: 9, border: '2px solid ' + C.gray200, background: C.white,
                                   color: C.gray500, fontSize: 17, cursor: 'pointer', touchAction: 'manipulation' }}>✎</button>
                      )}
                    </div>
                  </div>
                  <PruefpunktInput pp={pp} onChange={(c) => handleChange(pp, c)} kompakt teil='rest'/>
                  <AnlagenVorschlag regel={regelFuer(modulCode, pp.pos_nummer, lage, kapitelTrafo)} pp={pp}
                    onUebernehmen={(opt) => handleChange(pp, { wert_auswahl: opt, in_ordnung: null, wert_modifiers: [] })}/>
                  {details ? (
                    <div style={{ marginTop: 8 }}>
                      <PruefpunktInfo modulCode={modulCode} pp={pp}/>
                      <BemerkungInput value={pp.bemerkung} onChange={(v) => handleChange(pp, { bemerkung: v })}/>
                      <FotoUploadPruefpunkt pruefpunktId={pp.id} stationId={stationId} protokollId={protokollId} userId={user?.id}
                        onAnzahl={(n) => meldeFotoAnzahl(pp.id, n)}/>
                      {belegFehlt && (
                        <div style={{ marginTop: 8, padding: '8px 10px', background: C.yellowBg, borderLeft: '4px solid ' + C.yellow, borderRadius: 6, fontSize: 14, color: C.navy }}>
                          <strong>{t('Bitte noch belegen:')}</strong> {t('ein Grund oben, eine Bemerkung oder ein Foto. Eines davon genügt — sonst kann das Büro den Mangel später nicht einordnen.')}
                        </div>
                      )}
                    </div>
                  ) : null}
                  {/* Unsichtbar mitlaufen, damit die Foto-Anzahl auch bei zugeklappter
                      Zeile bekannt ist (Beleg-Regel, Aufklappen bei vorhandenen Fotos). */}
                  {!details && (
                    <div style={{ display: 'none' }}>
                      <FotoUploadPruefpunkt pruefpunktId={pp.id} stationId={stationId} protokollId={protokollId} userId={user?.id}
                        onAnzahl={(n) => meldeFotoAnzahl(pp.id, n)}/>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div key={cat} style={{ marginBottom: 18 }}>
          {cat !== '_default' && <div style={{ fontWeight: 800, color: C.navy, fontSize: 15, marginBottom: 8, paddingTop: 12, borderTop: '2px solid ' + C.gray100, textTransform: 'uppercase', letterSpacing: 0.5 }}>{kategorieText(cat)}</div>}
          {grouped[cat].map(pp => {
            const istNichtOk = pp.in_ordnung === false;
            const isSaving = savingIds.has(pp.id);
            // Beleg-Regel: Ein Kreuz bei "n.i.O." allein sagt dem Buero zu wenig
            // und macht die spaetere Angebotserstellung zum Raten. Eines von
            // dreien genuegt: Grund angekreuzt, Bemerkung getippt oder Foto.
            const belegFehlt = istNichtOk
              && !BEGRUENDUNG_KOMMT_VON_DER_APP.includes(pp.pos_nummer)
              && !(Array.isArray(pp.wert_modifiers) && pp.wert_modifiers.length > 0)
              && !(pp.bemerkung && pp.bemerkung.trim() !== '')
              && !(fotoAnzahl[pp.id] > 0);
            return (
              <div key={pp.id} ref={el => { punktRefs.current[pp.id] = el; }}
                style={{ padding: 12, marginBottom: 8, background: istNichtOk ? C.redBg : C.gray100, borderRadius: 8, borderLeft: '4px solid ' + (istNichtOk ? C.red : C.gray200), scrollMarginTop: 90 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, marginBottom: 8 }}>
                  <div style={{ flex: 1 }}>
                    {/* Kategorie am Pruefpunkt selbst. Die Ueberschrift weiter
                        oben scrollt weg - dadurch wirkten 01.1.4 ("Waende") und
                        01.2.2 ("Dach") wie derselbe Pruefpunkt. */}
                    {cat !== '_default' && (
                      <div style={{ fontSize: 12, fontWeight: 700, color: C.gray500, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 2 }}>
                        {kategorieText(cat)}
                      </div>
                    )}
                    <div style={{ fontWeight: 700, color: C.navy, fontSize: 17, lineHeight: 1.35 }}>
                      {pp.pos_nummer && <span style={{ color: C.gray500, marginRight: 6 }}>{anzeigeNummer(pp, nummern)}</span>}
                      {ppText(modulCode, pp, 'bezeichnung')}
                      {pp.einheit && <span style={{ marginLeft: 6, fontSize: 13, color: C.gray500 }}>[{pp.einheit}]</span>}
                      {/* Vom Buero fuer den Druck ausgenommen (Migration 77).
                          Nur fuers Buero sichtbar - am Protokoll aendert es nichts. */}
                      {isBuero && pp.nicht_drucken && (
                        <span style={{ marginLeft: 8, padding: '2px 8px', borderRadius: 999, background: C.orangeBg,
                                       color: C.orangeText, fontSize: 12, fontWeight: 800, whiteSpace: 'nowrap' }}>
                          {t('nicht im Kundendruck')}
                        </span>
                      )}
                    </div>
                    {/* Erklaertext aus der Vorlage. Ohne ihn wirkten Fragen
                        unfertig (02.11) oder doppelt (03.4 / 03.6), und der
                        Sollwert bei 06.3 fehlte ganz. */}
                    {pp.beschreibung && (
                      <div style={{ fontSize: 15, color: C.gray700, marginTop: 4, lineHeight: 1.45 }}>
                        {ppText(modulCode, pp, 'beschreibung')}
                      </div>
                    )}
                    <PruefpunktInfo modulCode={modul?.modul_vorlagen?.code} pp={pp}/>
                    <VorherHinweis modulCode={modulCode} bezugsId={modul?.bezugs_id} pp={pp}/>
                  </div>
                  {isSaving && <span style={{ fontSize: 12, color: C.gray500 }}>{t('speichert...')}</span>}
                </div>
                <PruefpunktAutoHinweis pp={pp} config={config} msFelder={msFelder} trafos={trafos} geschwister={sichtbare}/>
                <AnlagenVorschlag regel={regelFuer(modulCode, pp.pos_nummer, lage, kapitelTrafo)} pp={pp}
                  onUebernehmen={(opt) => handleChange(pp, { wert_auswahl: opt, in_ordnung: null, wert_modifiers: [] })}/>
                {modulCode === UESP_MODUL && String(pp.pos_nummer || '').trim() === UESP_POS && (
                  <div style={{ padding: '8px 10px', background: C.gray100, borderLeft: '3px solid ' + C.gray300, borderRadius: 6, fontSize: 14, color: C.gray700, marginBottom: 6 }}>
                    {isBuero
                      ? t('Bewertung automatisch: Sicherung ist = Sicherung soll → i.O., sonst n.i.O. Einen Soll-Wert, den die App noch nicht kennt, hier eintragen – er wird für diesen Typ gemerkt.')
                      : t('Bitte Hersteller, Typ und die vorhandene Sicherung eintragen. Die Bewertung kommt automatisch (den Soll-Wert ergänzt bei Bedarf das Büro).')}
                  </div>
                )}
                <PruefpunktInput pp={pp} onChange={(c) => handleChange(pp, c)}
                  ohneKnoepfe={!isBuero && modulCode === UESP_MODUL && String(pp.pos_nummer || '').trim() === UESP_POS}/>
                <div style={{ marginTop: 8 }}>
                  <BemerkungInput value={pp.bemerkung} onChange={(v) => handleChange(pp, { bemerkung: v })}/>
                </div>
                <FotoUploadPruefpunkt pruefpunktId={pp.id} stationId={stationId} protokollId={protokollId} userId={user?.id}
                  onAnzahl={(n) => meldeFotoAnzahl(pp.id, n)}/>
                {belegFehlt && (
                  <div style={{ marginTop: 10, padding: '10px 12px', background: C.yellowBg, borderLeft: '4px solid ' + C.yellow, borderRadius: 6, fontSize: 15, color: C.navy, lineHeight: 1.45 }}>
                    <strong>{t('Bitte noch belegen:')}</strong> {t('ein Grund oben, eine Bemerkung oder ein Foto. Eines davon genügt — sonst kann das Büro den Mangel später nicht einordnen.')}
                  </div>
                )}
                {istNichtOk && <span style={{ fontSize: 13, fontWeight: 800, color: C.red, background: C.redBg, padding: '3px 8px', borderRadius: 4, marginTop: 8, display: 'inline-block' }}>{t('Mangel angelegt')}</span>}
              </div>
            );
          })}
        </div>
      ))}
      </fieldset>
    </div>
  );
}

// === Was bei der letzten Wartung herauskam ===================================
// Nur Anzeige (Vorschlag 7) - uebernommen wird nichts.
// Absprachen aus dem Wartungsvertrag (Migration 45) - OHNE Preise, ueber die
// Datenbankfunktion vertrag_absprachen. Zuletzt geladener Stand liegt im Geraet,
// damit er auch ohne Netz da ist. Fehlt die Funktion noch: nichts anzeigen.
function KundenAbsprachen({ stationId }) {
  const [a, setA] = useState(() => (stationId ? absprachenAusCache(stationId) : null));
  useEffect(() => {
    if (!stationId) return undefined;
    let aktiv = true;
    setA(absprachenAusCache(stationId));
    absprachenLaden(stationId).then(d => { if (aktiv) setA(d); }).catch(() => { /* ohne Netz / ohne Migration: Cache */ });
    return () => { aktiv = false; };
  }, [stationId]);
  if (!a) return null;
  const vertraege = [a.wartungsvertrag && t('Wartungsvertrag'), a.betriebsfuehrung && t('Betriebsführungsvertrag'), a.bereitschaft && t('Bereitschaft (Notdienst)')].filter(Boolean);
  const zeiten = (a.abschaltzeiten || []).map(k => ABSCHALTZEITEN.find(x => x.key === k)?.text).filter(Boolean).map(x => t(x));
  const l = a.leistungen || {};
  const leistungen = [
    ...LEISTUNGEN.filter(x => leistungGewaehlt(l, x.key)).map(x => t(x.text)),
    l.oelprobe && t(OELPROBE_VERTRAG.find(x => x.value === l.oelprobe)?.label || ''),
  ].filter(Boolean);
  const punkte = [
    a.freigabe_schriftlich && t('Schriftliche Freigabe des Kunden vor der Wartung nötig'),
    a.kunde_informiert_vnb && t('Kunde informiert und beauftragt den Netzbetreiber'),
    a.kunde_stellt_schaltberechtigten && t('Kunde stellt Anlagenverantwortlichen und Schaltberechtigten'),
  ].filter(Boolean);
  if (vertraege.length === 0 && punkte.length === 0 && zeiten.length === 0 && !a.hinweis_monteur && leistungen.length === 0) return null;
  return (
    <div style={{ padding: '12px 14px', background: C.blueBg, borderLeft: '4px solid ' + C.blue, borderRadius: 8, marginBottom: 14, fontSize: 15, color: C.navy, lineHeight: 1.45 }}>
      <div style={{ fontWeight: 800, marginBottom: 6 }}>{t('Absprachen mit dem Kunden')}</div>
      {vertraege.length > 0 && <div><strong>{t('Verträge:')}</strong> {vertraege.join(', ')}</div>}
      {leistungen.length > 0 && <div><strong>{t('Im Wartungsvertrag enthalten:')}</strong> {leistungen.join(', ')}</div>}
      {zeiten.length > 0 && <div><strong>{t('Abschaltung möglich:')}</strong> {zeiten.join(', ')}{a.abschalt_notiz ? ' · ' + a.abschalt_notiz : ''}</div>}
      {punkte.map(x => <div key={x}>• {x}</div>)}
      {a.hinweis_monteur && <div style={{ marginTop: 4, fontWeight: 700 }}>{a.hinweis_monteur}</div>}
    </div>
  );
}

function VorherHinweis({ modulCode, bezugsId, pp }) {
  const vor = useVorWartung();
  const e = vor?.punkte?.[vorWartungSchluessel(modulCode, bezugsId, pp.pos_nummer)];
  if (!e) return null;
  return (
    <div style={{ marginTop: 5, fontSize: 13, lineHeight: 1.4, color: e.nio ? C.red : C.gray500 }}>
      {t('Letzte Wartung')}{vor.datum ? ' (' + datumKurz(vor.datum) + ')' : ''}:{' '}
      <strong>{e.e || '–'}</strong>{e.h ? ' · ' + e.h : ''}
    </div>
  );
}

// === Offene Punkte ueber alle Kapitel ========================================
// Anna-Lena 15.09.2026 (Vorschlag 2): Bei rund 140 Punkten ist das Suchen nach
// dem, was noch fehlt, die eigentliche Arbeit. Die Liste zeigt NUR, was fehlt -
// sie beantwortet nichts (ein Sammel-"i.O." ist bewusst nicht gewollt).
function OffenePunkte({ module, trafos, config, vnbName, lage, onWaehle, onSchliessen }) {
  const gruppen = module.map((m, idx) => {
    const alle = m.pruefpunkte || [];
    const sichtbar = alle.filter(pp => istSichtbar(pp, alle, trafos, { config, vnbName, lage, trafo: trafoDesKapitels(m, trafos) }));
    const offen = sichtbar.filter(pp => !istBeantwortet(pp));
    return { m, idx, offen, nummern: anzeigeNummern(m.modul_vorlagen?.code, sichtbar) };
  }).filter(g => g.offen.length > 0);
  const gesamt = gruppen.reduce((n, g) => n + g.offen.length, 0);
  return (
    <div role="dialog" onClick={onSchliessen}
      style={{ position: 'fixed', inset: 0, background: 'rgba(10,22,40,0.55)', zIndex: 300, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', padding: '40px 12px' }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: C.white, borderRadius: 12, width: '100%', maxWidth: 640, maxHeight: 'calc(100vh - 80px)', display: 'flex', flexDirection: 'column', boxShadow: '0 10px 40px rgba(0,0,0,0.3)' }}>
        <div style={{ padding: '14px 16px', borderBottom: '1px solid ' + C.gray200, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
          <div style={{ fontWeight: 800, fontSize: 17, color: C.navy }}>
            {gesamt === 0 ? t('Alle Prüfpunkte sind beantwortet') : t('{n} offene Prüfpunkte', { n: gesamt })}
          </div>
          <button type="button" onClick={onSchliessen}
            style={{ minHeight: 44, padding: '8px 14px', borderRadius: 8, border: '1.5px solid ' + C.gray200, background: C.white, color: C.navy, fontWeight: 700, cursor: 'pointer' }}>
            {t('Schließen')}
          </button>
        </div>
        <div style={{ overflowY: 'auto', padding: '8px 16px 16px' }}>
          {gesamt === 0 && <div style={{ padding: 20, color: C.green, fontWeight: 700 }}>{t('✓ Nichts mehr offen.')}</div>}
          {gruppen.map(({ m, idx, offen, nummern }) => (
            <div key={m.id} style={{ marginTop: 12 }}>
              <div style={{ fontWeight: 800, color: C.navy, fontSize: 14, marginBottom: 6 }}>
                {modulName(m.modul_vorlagen?.code, m.modul_vorlagen?.name) || m.modul_vorlagen?.code}
                {m.bezugs_label ? ' · ' + m.bezugs_label : ''}
                <span style={{ marginLeft: 8, color: C.gray500, fontWeight: 600 }}>({offen.length})</span>
              </div>
              {offen.map(pp => (
                <button key={pp.id} type="button" onClick={() => onWaehle(idx, pp.id)}
                  style={{ display: 'block', width: '100%', textAlign: 'left', minHeight: 44, padding: '9px 12px', marginBottom: 5,
                           border: '1px solid ' + C.gray200, borderRadius: 8, background: C.gray100, color: C.navy, fontSize: 14, cursor: 'pointer', touchAction: 'manipulation' }}>
                  {pp.pos_nummer && <span style={{ color: C.gray500, marginRight: 8, fontWeight: 700 }}>{anzeigeNummer(pp, nummern)}</span>}
                  {ppText(m.modul_vorlagen?.code, pp, 'bezeichnung')}
                </button>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// === Ladeseite, die nie "haengt" ==============================================
// Nach 8 Sekunden erscheint ein klarer Hinweis mit "Nochmal" und "Zurück".
// Vorher blieb nur "Lade Protokoll..." stehen - der Monteur schloss die App.
function LadeSeite({ text, onZurueck, onNochmal }) {
  const [lange, setLange] = useState(false);
  useEffect(() => {
    const zeit = setTimeout(() => setLange(true), 8000);
    return () => clearTimeout(zeit);
  }, []);
  return (
    <div style={{ minHeight: '100vh', background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ textAlign: 'center', maxWidth: 360 }}>
        <div style={{ color: C.gray500, fontSize: 16 }}>{text}</div>
        {lange && (
          <>
            <div style={{ marginTop: 14, color: C.navy, fontSize: 15, lineHeight: 1.45 }}>
              {t('Das dauert ungewöhnlich lange – vermutlich schwaches Netz. Eingaben gehen nicht verloren.')}
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 14, flexWrap: 'wrap' }}>
              {onNochmal && (
                <button type="button" onClick={() => { setLange(false); onNochmal(); }}
                  style={{ minHeight: 48, padding: '10px 18px', borderRadius: 8, border: 'none', background: C.green, color: C.white, fontWeight: 800, fontSize: 15, cursor: 'pointer' }}>
                  {t('Nochmal versuchen')}
                </button>
              )}
              {onZurueck && (
                <button type="button" onClick={onZurueck}
                  style={{ minHeight: 48, padding: '10px 18px', borderRadius: 8, border: 'none', background: C.gray100, color: C.navy, fontWeight: 700, fontSize: 15, cursor: 'pointer' }}>
                  &lt; {t('Zurück')}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// === Speichern-Knopf ==========================================================
// Anna-Lena nach dem Testlauf 13.09.2026: "Daten verloren, weil kein Speicher-
// Button im Wartungsprotokoll war." Gespeichert wurde zwar automatisch - aber
// niemand konnte SEHEN, ob es geklappt hat, und Eingaben, die gerade noch
// kurz warteten, gingen beim Schliessen verloren. Der Knopf gibt alles sofort
// ab, sendet und sagt klar, was Sache ist.
function SpeicherKnopf({ protokollId }) {
  const [laeuft, setLaeuft] = useState(false);
  const [meldung, setMeldung] = useState(null);   // { art: 'ok'|'wartet'|'fehler', text }
  // Nur NEUE Ablehnungen melden - alte (andere Protokolle, Vortage) sollen
  // nicht jedes "Alles gespeichert" verhindern.
  const abgelehntVorher = useRef(abgelehnteAnzahl());

  const speichern = async () => {
    if (laeuft) return;
    setLaeuft(true); setMeldung(null);
    try {
      // Fotos laufen parallel mit (hoechstens 20 s abwarten).
      const fotoLauf = navigator.onLine
        ? Promise.race([fotosAbarbeiten().catch(() => 0), new Promise(res => setTimeout(res, 20000))]) : null;
      const r = await jetztSpeichern();
      if (fotoLauf) await fotoLauf;
      let fotos = { wartend: 0, abgelehnt: 0 };
      try { fotos = await fotoZaehler(protokollId); } catch { /* Geraet nicht lesbar */ }
      if (r.offen === 0 && r.abgelehnt <= abgelehntVorher.current && !r.voll) {
        if (fotos.abgelehnt > 0) {
          setMeldung({ art: 'fehler', voll: true, text: t('Alle Eingaben gespeichert. {n} Foto(s) wurden vom Server abgelehnt – beim Foto antippen.', { n: fotos.abgelehnt }) });
          return;
        }
        if (fotos.wartend > 0) {
          setMeldung({ art: 'wartet', text: t('Alle Eingaben gespeichert. {n} Foto(s) warten noch auf Netz und gehen automatisch raus.', { n: fotos.wartend }) });
          return;
        }
      }
      const uhr = new Date().toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
      if (r.abgelehnt > abgelehntVorher.current) {
        const erster = abgelehnteLesen()[0];
        setMeldung({ art: 'fehler', text: t('{n} Eingabe(n) hat die Datenbank abgelehnt', { n: r.abgelehnt })
          + (erster ? ' (' + erster.was + ': ' + erster.fehler + ')' : '') + '. ' + t('Sie sind auf dem Gerät aufbewahrt.') });
      } else if (r.voll && r.offen > 0) {
        setMeldung({ art: 'fehler', voll: true, text: t('Gerätespeicher voll! {n} Eingabe(n) sind nur vorläufig gemerkt. App nicht schließen, bis wieder Netz da ist und alles übertragen wurde.', { n: r.offen }) });
      } else if (r.offen === 0) {
        setMeldung({ art: 'ok', text: t('✓ Alles gespeichert ({uhr} Uhr)', { uhr }) });
      } else if (!r.online) {
        setMeldung({ art: 'wartet', text: t('Kein Netz: {n} Eingabe(n) sind sicher auf dem Gerät gespeichert und gehen automatisch raus.', { n: r.offen }) });
      } else {
        setMeldung({ art: 'wartet', text: t('{n} Eingabe(n) sind auf dem Gerät gespeichert, die Übertragung läuft noch.', { n: r.offen }) });
      }
    } finally {
      setLaeuft(false);
    }
  };

  const erneutSenden = async () => {
    if (!window.confirm(t('Die abgelehnten Eingaben noch einmal senden?') + '\n\n'
      + t('Nur sinnvoll, wenn das Büro den Grund behoben hat. Neuere Eingaben bleiben erhalten.'))) return;
    setLaeuft(true);
    try { await abgelehnteErneutSenden(); } finally { setLaeuft(false); }
    abgelehntVorher.current = abgelehnteAnzahl();
    speichern();
  };

  useEffect(() => {
    if (!meldung || meldung.art === 'fehler') return undefined;
    const zeit = setTimeout(() => setMeldung(null), 8000);
    return () => clearTimeout(zeit);
  }, [meldung]);

  const farbe = meldung?.art === 'ok' ? { bg: C.greenBg, fg: C.green }
    : meldung?.art === 'wartet' ? { bg: C.yellowBg, fg: C.navy } : { bg: C.redBg, fg: C.red };

  return (
    <div style={{ position: 'relative' }}>
      <button type="button" onClick={speichern} disabled={laeuft}
        style={{ minHeight: 48, padding: '10px 18px', background: laeuft ? C.gray300 : C.green, color: C.white,
                 border: 'none', borderRadius: 8, fontWeight: 800, fontSize: 15, cursor: laeuft ? 'wait' : 'pointer',
                 touchAction: 'manipulation', whiteSpace: 'nowrap' }}>
        {laeuft ? t('speichert …') : t('Speichern')}
      </button>
      {meldung && (
        <div role="status" style={{ position: 'absolute', bottom: 'calc(100% + 10px)', right: 0, width: 300, maxWidth: '85vw',
                                    padding: '11px 13px', borderRadius: 9, fontSize: 14, fontWeight: 700, lineHeight: 1.45,
                                    boxShadow: '0 6px 22px rgba(10,22,40,0.22)', background: farbe.bg, color: farbe.fg, zIndex: 120 }}>
          {meldung.text}
          {meldung.art === 'fehler' && !meldung.voll && (
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button type="button" onClick={erneutSenden}
                style={{ minHeight: 40, padding: '6px 12px', borderRadius: 7, border: 'none', background: C.red, color: C.white, fontWeight: 800, cursor: 'pointer' }}>
                {t('Erneut senden')}
              </button>
              <button type="button" onClick={() => setMeldung(null)}
                style={{ minHeight: 40, padding: '6px 12px', borderRadius: 7, border: '1.5px solid ' + C.gray200, background: C.white, color: C.gray700, fontWeight: 700, cursor: 'pointer' }}>
                {t('Schließen')}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function ProtokollFormPage({ protokollId, onBack }) {
  useSprache();   // bei Sprachwechsel neu zeichnen
  const { protokoll, module: initialModule, loading, error, reload: protokollNeuLaden } = useProtokoll(protokollId);
  // alleModule = alles aus dem Protokoll; module (weiter unten) = die angezeigten
  // Kapitel (ohne Oelwanne eines Giessharz-Trafos).
  const [alleModule, setModule] = useState([]);
  const [activeTabIdx, setActiveTabIdx] = useState(0);
  const [protokollLocal, setProtokollLocal] = useState(null);
  const [offeneListe, setOffeneListe] = useState(false);
  const [zielPunktId, setZielPunktId] = useState(null);
  const zielErreicht = useCallback(() => setZielPunktId(null), []);

  // Wie viele Antworten warten noch auf Netz? Der Monteur soll sehen, dass
  // nichts verloren ist -- und selbst nachschieben koennen.
  const [wartend, setWartend] = useState(wartendeAnzahl());
  useEffect(() => {
    starteAutomatik();
    setWartend(wartendeAnzahl());
    return beiAenderung(setWartend);
  }, []);
  const [fotosWartend, setFotosWartend] = useState(0);
  const [fotosAbgelehnt, setFotosAbgelehnt] = useState(0);
  useEffect(() => {
    let aktiv = true;
    const zaehlen = () => fotoZaehler(protokollId)
      .then(z => { if (aktiv) { setFotosWartend(z.wartend); setFotosAbgelehnt(z.abgelehnt); } })
      .catch(() => { /* Geraet nicht lesbar - alten Stand behalten */ });
    zaehlen();
    const ab = beiFotoAenderung(zaehlen);
    return () => { aktiv = false; ab(); };
  }, [protokollId]);

  const stationId = protokoll?.station_id;
  const vorWartung = useVorWartungLaden(protokoll);
  // ausCache: Beim Oeffnen sofort den zuletzt geladenen Stand zeigen und im
  // Hintergrund nachladen (nur hier - im Stammdaten-Formular waere ein alter
  // Stand beim Speichern gefaehrlich).
  const s = useStation(stationId, { ausCache: true });

  // Module aus der Datenbank uebernehmen.
  //
  // Beim ersten Laden komplett. Danach werden nur NEU dazugekommene Kapitel
  // angehaengt - die bereits offenen behalten ihre lokalen Antworten, sonst
  // wuerde ein Nachladen die Eingaben des Monteurs ueberschreiben.
  //
  // Frueher stand hier "nur wenn module.length === 0". Damit tauchte ein
  // nachgetragener Trafo NICHT auf: Es gab ja schon Kapitel, also passierte
  // nichts. Der Monteur legte den Trafo an und sah ihn nirgends.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!initialModule || initialModule.length === 0) return;
    setModule(vorhanden => {
      if (vorhanden.length === 0) return initialModule;
      // Seit v54 kann der erste Stand aus dem Geraete-Zwischenspeicher kommen.
      // Kommt danach der frische Stand vom Server, wird er uebernommen - ausser
      // bei Punkten, die der Monteur hier schon angefasst hat (letzterStand).
      // Wartendes aus der Warteschlange steckt im Server-Stand schon drin.
      const angefasst = letzterStand.current || {};
      const frischNachId = new Map(initialModule.map(m => [m.id, m]));
      const aktualisiert = vorhanden.map(m => {
        const frisch = frischNachId.get(m.id);
        if (!frisch) return m;
        // Kapitel-Angaben (z. B. Zuordnung zum Trafo, Migration 70) vom Server.
        return { ...m, ...frisch, pruefpunkte: (frisch.pruefpunkte || []).map(p => (angefasst[p.id] ? { ...p, ...angefasst[p.id] } : p)) };
      });
      const bekannt = new Set(vorhanden.map(m => m.id));
      const neue = initialModule.filter(m => !bekannt.has(m.id));
      // Entfernte Kapitel (Verteilung entfernt) fallen weg, und neue stehen an
      // ihrem Platz statt hinten (Verteilung direkt hinter der NSHV, v106).
      return [...aktualisiert.filter(m => frischNachId.has(m.id)), ...neue]
        .sort((a, b) => (a.reihenfolge ?? 0) - (b.reihenfolge ?? 0));
    });
  }, [initialModule]);

  useEffect(() => {
    // Frischer Stand vom Server ersetzt den aus dem Zwischenspeicher, solange
    // hier noch nichts am Protokollkopf/Abschluss geaendert wurde.
    if (protokoll && (!protokollLocal || !kopfGeaendert.current)) setProtokollLocal(protokoll);
    // Nur wenn ein frischer Stand kommt - nicht bei jeder lokalen Aenderung
    // (sonst sprang z. B. "angefangen" sofort wieder auf "geplant" zurueck).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [protokoll]);

  // Nach "weitere Verteilung": in das neue Kapitel springen, sobald es da ist.
  const [zielKapitelId, setZielKapitelId] = useState(null);
  useEffect(() => {
    if (!zielKapitelId) return;
    const liste = angezeigteKapitel(alleModule, s.station ? s.trafos : null, protokollId);
    const i = liste.findIndex(m => m.id === zielKapitelId);
    if (i >= 0) { setActiveTabIdx(i); setZielKapitelId(null); window.scrollTo({ top: 0, behavior: 'smooth' }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alleModule, zielKapitelId]);

  const { isBuero } = useAuth();
  // Neuester lokaler Stand je Pruefpunkt - Grundlage fuer jede Aenderung
  // (siehe handleChange im ModulPanel).
  const letzterStand = useRef({});
  const kopfGeaendert = useRef(false);

  // Geplante Wartung -> "angefangen". Der Monteur oeffnet sie = begonnen. Das
  // Buero schaut oft nur hinein - dort erst bei der ersten Eingabe.
  const begonnen = useRef(false);
  const alsBegonnen = async () => {
    if (begonnen.current) return;
    const st = (protokollLocal || protokoll)?.status;
    if (!['vorbereitet', 'zugewiesen'].includes(st)) return;
    begonnen.current = true;
    const r = await alsBegonnenMarkieren(protokollId);
    if (r === 'geaendert') setProtokollLocal(p => ({ ...(p || protokoll), status: 'entwurf' }));
    if (r === 'fehler') begonnen.current = false;   // bei der naechsten Eingabe erneut
  };
  useEffect(() => {
    if (protokoll?.id && !isBuero) alsBegonnen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [protokoll?.id, isBuero]);

  // Den Stand dieser Seite auf dem Geraet nachfuehren (2 s nach der letzten
  // Aenderung und beim Verlassen), damit ein erneutes Oeffnen ohne Netz die
  // eigenen Eingaben zeigt - siehe protokollZwischenspeichern.
  const zwischenstand = useRef(null);
  useEffect(() => {
    if (!protokollLocal || alleModule.length === 0) return undefined;
    zwischenstand.current = { protokoll: protokollLocal, module: alleModule };
    const zeit = setTimeout(() => {
      protokollZwischenspeichern(protokollId, protokollLocal, alleModule);
      zwischenstand.current = null;
    }, 2000);
    return () => clearTimeout(zeit);
  }, [protokollId, protokollLocal, alleModule]);
  useEffect(() => () => {
    const z = zwischenstand.current;
    if (z) protokollZwischenspeichern(protokollId, z.protokoll, z.module);
  }, [protokollId]);

  const onUpdateLocal = (updatedPp) => {
    setModule(mods => mods.map(m => ({
      ...m,
      pruefpunkte: (m.pruefpunkte || []).map(pp => pp.id === updatedPp.id ? { ...pp, ...updatedPp } : pp)
    })));
  };

  // Nur noch auf das PROTOKOLL warten, nicht zusaetzlich auf die Stationsdaten
  // (Testlauf 13.09.2026: "es kam Lädt" - bei schlechtem Netz bis zu einer
  // Minute). Die Stammdaten-Bloecke zeigen ihr eigenes "wird geladen".
  if (loading) {
    return <LadeSeite text={t('Lade Protokoll …')} onZurueck={onBack} onNochmal={protokollNeuLaden}/>;
  }
  // Fehler beim ERSTEN Laden: Hinweis mit "Nochmal" und "Zurück" - vorher
  // stand nur eine rote Zeile da, ohne Weg zurück. Scheitert ein spaeteres
  // Nachladen, bleibt das Protokoll stehen (Hinweis siehe unten im Kopf).
  if (!protokoll) {
    return (
      <div style={{ minHeight: '100vh', background: C.bg, padding: 32 }}>
        <div style={{ color: C.red, marginBottom: 16, fontSize: 15 }}>
          ! {t('Das Protokoll konnte nicht geladen werden')}{error ? ': ' + error : '.'}
        </div>
        <button onClick={protokollNeuLaden} style={{ padding: '10px 18px', background: C.green, color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 15, marginRight: 10 }}>{t('Nochmal versuchen')}</button>
        <button onClick={onBack} style={{ padding: '10px 18px', background: C.gray100, color: C.navy, border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 15 }}>&lt; {t('Zurück')}</button>
      </div>
    );
  }

  // Nur sichtbare Punkte zaehlen - wie in den Reitern. Vorher zaehlte die
  // Kopfzeile ausgeblendete Folgefragen mit und kam nie auf "alles fertig".
  // Trafos unbekannt (Station nicht geladen) = null: dann nichts ausblenden.
  const trafosBekannt = s.station ? s.trafos : null;
  const module = angezeigteKapitel(alleModule, trafosBekannt, protokollId);
  // Wie bei den Trafos: Konfiguration unbekannt = nichts ausblenden (02.1 Gasdruck).
  const configBekannt = s.station ? s.config : null;
  const vnbBekannt = s.station ? s.vnbName : undefined;
  const lageBekannt = s.station ? anlagenLage(configBekannt, s.msFelder, trafosBekannt) : undefined;
  const kontextVon = (m) => ({ config: configBekannt, vnbName: vnbBekannt, lage: lageBekannt, trafo: trafoDesKapitels(m, trafosBekannt) });
  const sichtbareJeModul = module.map(m => (m.pruefpunkte || []).filter(pp => istSichtbar(pp, m.pruefpunkte || [], trafosBekannt, kontextVon(m))));
  const allTotalCount = sichtbareJeModul.reduce((n, l) => n + l.length, 0);
  const allFilledCount = sichtbareJeModul.reduce((n, l) => n + l.filter(istBeantwortet).length, 0);

  const aktuellesProtokoll = protokollLocal || protokoll;
  const protokollStatus = aktuellesProtokoll?.status;
  const isAbgeschlossen = protokollStatus === 'abgeschlossen' || protokollStatus === 'monteur_abgeschlossen' || protokollStatus === 'buero_freigegeben';
  // Beim Wechsel des Moduls immer oben anfangen. Sonst landet man mitten im
  // neuen Kapitel, weil der Browser die Scrollposition behaelt - Anna-Lena:
  // "beim wechseln in ein neues Kapitel bitte immer oben auf der Seite anfangen".
  const zeigeModul = (idx) => {
    setActiveTabIdx(idx);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const istAbschlussTab = activeTabIdx === module.length;

  // "Trafo fehlt?" nur im Trafo-Kapitel (Anna-Lena 18.09.2026). Hat die Station

  // noch gar kein Trafo-Kapitel, steht der Knopf in "MS-Anlage" (sonst im ersten).

  const codeVon = (m) => m?.modul_vorlagen?.code || '';

  const hatTrafoKapitel = module.some(m => codeVon(m) === '04-TRAFO-SICHT');

  const ersatzKapitel = module.findIndex(m => codeVon(m) === '02-MS-ANLAGE');

  const trafoKnopfHier = hatTrafoKapitel

    ? codeVon(module[activeTabIdx]) === '04-TRAFO-SICHT'

    : activeTabIdx === (ersatzKapitel >= 0 ? ersatzKapitel : 0);

  // Verlassen nur mit vorherigem Speichern: Was noch in einem Feld wartet,
  // wird abgegeben, und es wird kurz gesendet (hoechstens ~4 s, ohne Netz sofort).
  const verlassen = async () => {
    try { await jetztSpeichern({ maxWartenMs: 3500 }); } catch { /* egal - es ist gemerkt */ }
    if (onBack) onBack();
  };

  // Name des naechsten Moduls fuer die Beschriftung des Weiter-Knopfs.
  const naechstesModul = module[activeTabIdx + 1];
  const naechstesModulName = naechstesModul?.pseudo ? t('Wandler / Zähler') : naechstesModul
    ? [modulName(naechstesModul.modul_vorlagen?.code, naechstesModul.modul_vorlagen?.name) || naechstesModul.modul_vorlagen?.code, naechstesModul.bezugs_label]
        .filter(Boolean).join(' · ')
    : '';

  return (
    <VorWartungContext.Provider value={vorWartung}>
    <div style={{ background: C.bg, minHeight: '100vh', fontFamily: "system-ui,-apple-system,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif" }}>
      <div style={{ background: C.navy, padding: '14px', boxShadow: '0 2px 14px rgba(0,0,0,0.22)' }}>
        <div style={{ maxWidth: 1400, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button onClick={verlassen} style={zurueckStil}>‹ {t('Zurück')}</button>
            <div>
              <div style={{ color: C.white, fontWeight: 800, fontSize: 16 }}>
                {protokoll.art === 'sichtpruefung' ? t('Sichtprüfungsprotokoll') : t('Wartungsprotokoll')}
                {protokollStatus === 'buero_freigegeben' && <span style={{ marginLeft: 10, fontSize: S.xs, fontWeight: 700, color: C.green, background: C.greenBg, padding: '2px 8px', borderRadius: 6, whiteSpace: 'nowrap' }}>{t('freigegeben')}</span>}
                {protokollStatus === 'monteur_abgeschlossen' && <span style={{ marginLeft: 10, fontSize: S.xs, fontWeight: 700, color: C.orangeText, background: C.orangeBg, padding: '2px 8px', borderRadius: 6, whiteSpace: 'nowrap' }}>{t('wartet auf Büro')}</span>}
                {protokollStatus === 'abgeschlossen' && <span style={{ marginLeft: 10, fontSize: S.xs, fontWeight: 700, color: C.green, background: C.greenBg, padding: '2px 8px', borderRadius: 6, whiteSpace: 'nowrap' }}>{t('abgeschlossen')}</span>}
                {(!protokollStatus || protokollStatus === 'entwurf') && <span style={{ marginLeft: 10, fontSize: S.xs, fontWeight: 700, color: C.blue, background: C.blueBg, padding: '2px 8px', borderRadius: 6, whiteSpace: 'nowrap' }}>{t('in Arbeit')}</span>}
              </div>
              <div style={{ color: C.onDark, fontSize: 14 }}>{protokoll.stationsbezeich || protokoll.station_id} · {datumAnzeige(protokoll.wartungs_datum)}</div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <div style={{ color: C.white, fontSize: 15 }}>{t('{fertig} / {gesamt} Prüfpunkte', { fertig: allFilledCount, gesamt: allTotalCount })}</div>
            {allFilledCount < allTotalCount && (
              <button type="button" onClick={() => setOffeneListe(true)}
                style={{ minHeight: 40, padding: '7px 14px', borderRadius: 8, border: 'none', background: C.yellow, color: C.navy, fontWeight: 800, fontSize: 14, cursor: 'pointer', touchAction: 'manipulation' }}>
                {t('Offene Punkte ({n})', { n: allTotalCount - allFilledCount })}
              </button>
            )}
          </div>
        </div>
      </div>

      {offeneListe && (
        <OffenePunkte module={module} trafos={trafosBekannt} config={configBekannt} vnbName={vnbBekannt} lage={lageBekannt}
          onSchliessen={() => setOffeneListe(false)}
          onWaehle={(idx, ppId) => { setOffeneListe(false); setActiveTabIdx(idx); setZielPunktId(ppId); }}/>
      )}

      {/* Vom Server abgelehnter Abschluss: nicht still verschwinden lassen. */}
      {abschlussAbgelehnt(protokollId) && (
        <div style={{ background: C.redBg, color: C.navy, padding: '12px 14px', fontSize: 15, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', borderLeft: '4px solid ' + C.red }}>
          <span style={{ flex: 1, minWidth: 220 }}>
            <strong style={{ color: C.red }}>{t('Der Abschluss wurde NICHT gespeichert.')}</strong>{' '}
            {abschlussAbgelehnt(protokollId).fehler} {t('Unterschrift und Bewertung sind auf dem Gerät aufbewahrt – bitte das Büro informieren.')}
          </span>
          <button type="button" onClick={() => abgelehnteErneutSenden().then(() => protokollNeuLaden())}
            style={{ minHeight: 44, padding: '8px 14px', background: C.white, border: '1.5px solid ' + C.red, color: C.red, borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 14 }}>
            {t('Erneut senden')}
          </button>
        </div>
      )}

      {/* Nachladen gescheitert: Das Protokoll bleibt stehen, Eingaben gehen
          nicht verloren - nur der Hinweis, dass der Stand evtl. nicht frisch ist. */}
      {error && (
        <div style={{ background: C.orangeBg, color: C.navy, padding: '10px 14px', fontSize: 14, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span>{t('Neu laden hat nicht geklappt ({fehler}). Du kannst weiterarbeiten.', { fehler: error })}</span>
          <button onClick={protokollNeuLaden} style={{ padding: '6px 12px', background: C.white, border: '1px solid ' + C.gray200, borderRadius: 6, cursor: 'pointer', fontSize: 14 }}>{t('Nochmal versuchen')}</button>
        </div>
      )}

      <div style={{ background: C.white, borderBottom: '1px solid ' + C.gray200, position: 'sticky', top: 0, zIndex: 50 }}>
        <div style={{ maxWidth: 1400, margin: '0 auto', display: 'flex', overflowX: 'auto' }}>
          {module.map((m, idx) => {
            // Auch im Reiter nur zaehlen, was tatsaechlich angezeigt wird -
            // sonst steht dort 13/15, obwohl zwei Fragen ausgeblendet sind.
            const sichtbar = (m.pruefpunkte || []).filter(pp => istSichtbar(pp, m.pruefpunkte || [], trafosBekannt, kontextVon(m)));
            const filled = sichtbar.filter(pp =>
              pp.wert_auswahl != null || pp.wert_zahl != null || pp.wert_bool != null || (pp.wert_text && pp.wert_text.length > 0)
            ).length;
            const total = sichtbar.length;
            const isFull = total > 0 && filled === total;
            const isActive = activeTabIdx === idx;
            if (m.pseudo) {
              const anzahl = (s.wandler || []).length + (Array.isArray(s.config?.zaehler) ? s.config.zaehler.length : 0);
              return (
                <button key={m.id} onClick={() => zeigeModul(idx)} style={{
                  flex: '1 0 auto', padding: '12px 14px',
                  background: isActive ? C.yellowBg : 'transparent',
                  color: isActive ? C.navy : C.gray500,
                  border: 'none', borderBottom: isActive ? '3px solid ' + C.yellow : '3px solid transparent',
                  cursor: 'pointer', fontSize: 14, fontWeight: isActive ? 800 : 600,
                  whiteSpace: 'nowrap', minWidth: 120, textAlign: 'left',
                }}>
                  <span style={{ display: 'block' }}>{t('Wandler / Zähler')}</span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3, fontSize: 12, fontWeight: 600, color: C.gray500 }}>
                    {s.station ? t('{n} erfasst', { n: anzahl }) : t('Stammdaten')}
                  </span>
                </button>
              );
            }
            return (
              <button key={m.id} onClick={() => zeigeModul(idx)} style={{
                flex: '1 0 auto', padding: '12px 14px',
                background: isActive ? C.yellowBg : 'transparent',
                color: isActive ? C.navy : C.gray500,
                border: 'none', borderBottom: isActive ? '3px solid ' + C.yellow : '3px solid transparent',
                cursor: 'pointer', fontSize: 14, fontWeight: isActive ? 800 : 600,
                whiteSpace: 'nowrap', minWidth: 120, textAlign: 'left',
              }}>
                {/* Lesbarer Kapitelname statt Programm-Code ("04-TRAFO-SICHT") */}
                <span style={{ display: 'block' }}>
                  {modulName(m.modul_vorlagen?.code, m.modul_vorlagen?.name) || m.modul_vorlagen?.code || t('Modul {nr}', { nr: idx + 1 })}
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3, fontSize: 12, fontWeight: 600, color: C.gray500 }}>
                  {m.bezugs_label && <span>{m.bezugs_label}</span>}
                  <span style={{ padding: '1px 6px', borderRadius: 6, background: isFull ? C.green : C.gray200, color: isFull ? C.white : C.gray700, fontWeight: 700 }}>
                    {filled}/{total}
                  </span>
                </span>
              </button>
            );
          })}
          {/* === Abschluss-Tab === */}
          <button onClick={() => zeigeModul(module.length)} style={{
            flex: '1 0 auto', padding: '12px 14px',
            background: istAbschlussTab ? C.yellowBg : 'transparent',
            color: istAbschlussTab ? C.navy : C.gray500,
            border: 'none', borderBottom: istAbschlussTab ? '3px solid ' + C.yellow : '3px solid transparent',
            cursor: 'pointer', fontSize: 14, fontWeight: istAbschlussTab ? 800 : 600,
            whiteSpace: 'nowrap', minWidth: 130, position: 'relative',
            borderLeft: '2px solid ' + C.gray200,
          }}>
            {t('ABSCHLUSS')}
            <span style={{ display: 'block', fontSize: 12, color: C.gray500, fontWeight: 500 }}>{t('Bewertung & Unterschrift')}</span>
          </button>
        </div>
      </div>

      {/* 180 px unten: Die Fussleiste ist am Handy zweizeilig und darf den
          Knopf "Wartung abschliessen" nicht verdecken. */}
      <div style={{ maxWidth: 1400, margin: '0 auto', padding: '20px 14px 180px' }}>
        {istAbschlussTab ? (
          <AbschlussBericht
            protokoll={aktuellesProtokoll}
            isLocked={isAbgeschlossen && !isBuero}
            unterschriftGesperrt={isAbgeschlossen}
            onProtokollUpdated={(patch) => { kopfGeaendert.current = true; setProtokollLocal(alt => ({ ...(alt || protokoll), ...patch })); }}
          />
        ) : module.length === 0 ? (
          <div style={{ background: C.white, padding: 30, borderRadius: 10, textAlign: 'center', color: C.gray500 }}>
            {t('Keine Module - bitte Stationskonfiguration pruefen.')}
          </div>
        ) : module[activeTabIdx]?.pseudo ? (
          <WandlerZaehlerPanel
            modul={module[activeTabIdx]}
            station={s.station}
            config={s.config}
            wandler={s.wandler}
            onStationReload={s.reload}
            stationFehler={s.station ? null : s.error}
            gesperrt={isAbgeschlossen && !isBuero}
          />
        ) : (
          <ModulPanel
            modul={module[activeTabIdx]}
            protokollId={protokollId}
            stationId={stationId}
            station={s.station}
            config={s.config}
            vnbName={s.station ? s.vnbName : undefined}
            msFelder={s.msFelder}
            trafos={trafosBekannt}
            wandler={s.wandler}
            onUpdateLocal={onUpdateLocal}
            onStationReload={s.reload}
            stationFehler={s.station ? null : s.error}
            letzterStand={letzterStand}
            onErsteEingabe={alsBegonnen}
            zielPunktId={zielPunktId}
            onZielErreicht={zielErreicht}
            gesperrt={isAbgeschlossen && !isBuero}
            abgeschlossenBuero={isAbgeschlossen && isBuero}
            onKapitelGeaendert={async (info) => {
              // Entfernt: zurueck auf das Kapitel davor (die Haupt-NSHV).
              if (info?.entfernt) setActiveTabIdx(i => Math.max(0, i - 1));
              await protokollNeuLaden();
            }}
          />
        )}
        {!istAbschlussTab && !isAbgeschlossen && codeVon(module[activeTabIdx]) === '03-NSHV'
          && codeVon(module[activeTabIdx + 1]) !== '03-NSHV' && (
          <VerteilungNachtragen protokollId={protokollId} stationId={stationId}
            onFertig={async (kapitel) => {
              if (kapitel?.id) setZielKapitelId(kapitel.id);
              await protokollNeuLaden();
            }} />
        )}
        {/* Nach dem Nachtragen direkt in das neue Kapitel springen - sonst
            sucht der Monteur, wo sein Trafo hin ist. */}
        {!istAbschlussTab && !isAbgeschlossen && trafoKnopfHier && (
          <TrafoNachtragen protokollId={protokollId} stationId={stationId}
            onFertig={async () => {
              s.reload();
              await protokollNeuLaden();
              setTimeout(() => zeigeModul(module.length), 300);
            }} />
        )}
      </div>

      {/* Fussleiste jetzt auch im ABSCHLUSS-Reiter - dort braucht es den
          Speichern-Knopf genauso. Nur "Weiter" entfaellt dort.
          Das Buero darf abgeschlossene Protokolle korrigieren (v68) - dann muss
          es auch den Speichern-Knopf und den Netz-Hinweis sehen (Pruefung 20.09.2026). */}
      {(!isAbgeschlossen || isBuero) && (
        <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, background: C.white, borderTop: '1px solid ' + C.gray200, boxShadow: '0 -2px 14px rgba(0,0,0,0.08)', padding: '12px 14px', zIndex: 100 }}>
          <div style={{ maxWidth: 1400, margin: '0 auto', display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {wartend > 0 || fotosWartend > 0 || fotosAbgelehnt > 0 ? (
              /* Ruhiger Hinweis, keine Fehlermeldung: Es ist nichts verloren,
                 der Monteur muss nichts tun. Der Knopf ist nur fuer die
                 Ungeduldigen. */
              <div style={{ flex: 1, minWidth: 240, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                            background: C.yellowBg, borderLeft: '4px solid ' + C.yellow, borderRadius: 6, padding: '8px 12px' }}>
                <span style={{ fontSize: 14, color: C.navy, lineHeight: 1.4 }}>
                  {wartend > 0 && <strong>{wartend === 1 ? t('1 Eingabe wartet auf Netz.') : t('{n} Eingaben warten auf Netz.', { n: wartend })}</strong>}{' '}
                  {fotosWartend > 0 && <strong>{t('{n} Foto(s) warten auf Netz.', { n: fotosWartend })}</strong>}{' '}
                  {fotosAbgelehnt > 0 && <strong style={{ color: C.red }}>{t('{n} Foto(s) abgelehnt – beim Foto antippen.', { n: fotosAbgelehnt })}</strong>}{' '}
                  {speicherIstVoll()
                    ? <strong style={{ color: C.red }}>{t('Gerätespeicher voll – App bitte nicht schließen, bis alles übertragen ist.')}</strong>
                    : t('Auf dem Gerät gespeichert, geht automatisch raus.')}
                </span>
                <button type="button" onClick={() => { abarbeiten(); fotosAbarbeiten().catch(() => {}); }}
                  style={{ minHeight: 38, padding: '7px 14px', border: '1.5px solid ' + C.navy, borderRadius: 7,
                           background: C.white, color: C.navy, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                  {t('Jetzt versuchen')}
                </button>
              </div>
            ) : (
              <div style={{ fontSize: 14, color: C.gray500, flex: 1 }}>
                {t('Alle Eingaben werden automatisch gespeichert. Bei "n.i.O." wird automatisch ein Mangel angelegt.')}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <SpeicherKnopf protokollId={protokollId}/>
              <button onClick={verlassen} style={{ padding: '10px 18px', background: C.gray100, color: C.navy, border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 15 }}>{t('Pause – später weiter')}</button>
              {/* Der Knopf fuehrt zum NAECHSTEN Modul, nicht ans Ende. Erst auf
                  dem letzten Modul geht es zum Abschluss. */}
              {!istAbschlussTab && !isAbgeschlossen && <button
                onClick={() => zeigeModul(Math.min(activeTabIdx + 1, module.length))}
                style={{ padding: '10px 22px', background: C.yellow, color: C.navy, border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 800, fontSize: 14, textAlign: 'left', lineHeight: 1.25 }}>
                {activeTabIdx < module.length - 1 ? (
                  <>
                    {t('Weiter')}
                    <span style={{ display: 'block', fontSize: 12, fontWeight: 600, opacity: 0.75 }}>
                      {naechstesModulName}
                    </span>
                  </>
                ) : t('Weiter zum Abschluss')}
              </button>}
            </div>
          </div>
        </div>
      )}
    </div>
    </VorWartungContext.Provider>
  );
}
